/**
 * sqlite3mt three-way merge plan and residual overlay
 *
 * Mechanical extraction from §96 §15.3–§15.5.
 *
 * This module implements the core merge logic for remote reconcile:
 * - resolveMergedPageCount is in dbview.ts (already implemented)
 * - buildMergePlan — the full three-way merge (§15.3)
 * - buildResidualOverlayAgainstRemote — pure logic (§15.4)
 * - ensureMetadataCarrierFrameIfNeeded — page-count-only carrier (§15.5.1)
 * - getFinalResultPageHash — helper for carrier resolution
 *
 * The MergePlan type and ManifestOperations interface are in types.ts.
 */

import { sha256 as nobleSha256 } from '@noble/hashes/sha2'

import {
    type ManifestOperations,
    type MergePlan,
    type PageHash,
    type Pgno,
    type Root,
    type WalSnapshot,
} from './types'
import { resolveMergedPageCount } from './dbview'
import { sdkConsole } from '../logger'
import {
    BTreePageType,
    buildIndexLeafPage,
    buildIndexLeafPageWithOverflow,
    buildLeafPage,
    buildLeafPageWithOverflow,
    extractCells,
    extractInteriorIndexCells,
    extractIndexCells,
    getPageType,
    mergePage1Header,
    mergeIndexCellSets,
    compareSqliteRecords,
    parseSqliteMaster,
    readVarint,
    splitIndexPagesWithOverflow,
    splitPages,
    splitPagesWithOverflow,
    tryColumnMerge,
    writeVarint,
    type BTreeCell,
    type IndexCell,
    type SqliteMasterEntry,
} from '../btreePageParser'

// ── §15.4 Residual overlay ─────────────────────────────────────

export interface ResidualOverlay {
    pages: Map<Pgno, PageHash>
    pageCount: number
}

/**
 * Build the residual overlay: the local workspace state remaining after a
 * successful reconcile, expressed against the new remote base.
 *
 * §15.4: For each page in the normalized local view:
 * - If the page is NOT in remoteChanged, keep it in the residual.
 * - If the page IS in remoteChanged, keep it only if the merged result
 *   differs from the remote hash (local win or custom merge).
 *
 * Pages above resultPageCount are excluded.
 */
export function buildResidualOverlayAgainstRemote(plan: MergePlan): ResidualOverlay {
    const nextPages = new Map<Pgno, PageHash>()

    for (const [pgno, localHash] of plan.liveLocalNorm) {
        if (pgno > plan.resultPageCount) continue

        if (!plan.remoteChanged.has(pgno) && !plan.resultHashByPgno.has(pgno)) {
            nextPages.set(pgno, localHash)
        }
    }

    for (const pgno of plan.remoteChanged) {
        if (pgno > plan.resultPageCount) continue

        const remoteHash = plan.remoteHashes.get(pgno) ?? null
        const finalHash = plan.resultHashByPgno.get(pgno) ?? remoteHash

        if (finalHash !== remoteHash) {
            if (finalHash != null) {
                nextPages.set(pgno, finalHash)
            }
        }
    }

    for (const [pgno, finalHash] of plan.resultHashByPgno) {
        if (pgno > plan.resultPageCount) continue
        if (plan.remoteChanged.has(pgno)) continue

        const remoteHash = plan.remoteHashes.get(pgno) ?? null
        if (finalHash !== remoteHash) {
            nextPages.set(pgno, finalHash)
        }
    }

    return {
        pages: nextPages,
        pageCount: plan.resultPageCount,
    }
}

// ── §15.3 buildMergePlan ───────────────────────────────────────

export interface BuildMergePlanArgs {
    baseRoot: Root
    baseVersion: bigint
    remoteRoot: Root
    remoteVersion: bigint
    wal: WalSnapshot
    localNorm: Map<Pgno, PageHash>
    localPageCount: number
    pendingDbDeltasIntegratedFrameMax: number
}

function setMergePlanDebugStage(
    manifest: ManifestOperations,
    stage: string,
): void {
    ;(manifest as ManifestOperations & { setDebugStage?: (stage: string) => void }).setDebugStage?.(stage)
}

function uint8ArrayEqual(a: Uint8Array, b: Uint8Array): boolean {
    if (a === b) return true
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false
    }
    return true
}

function compareBytesLex(left: Uint8Array, right: Uint8Array): number {
    const len = Math.min(left.length, right.length)
    for (let i = 0; i < len; i += 1) {
        if (left[i] !== right[i]) return left[i] - right[i]
    }
    return left.length - right.length
}

function canonicalCell(left: BTreeCell, right: BTreeCell): BTreeCell {
    const hashCmp = compareBytesLex(left.payloadHash, right.payloadHash)
    if (hashCmp !== 0) return hashCmp > 0 ? left : right
    const payloadCmp = compareBytesLex(left.payload, right.payload)
    return payloadCmp >= 0 ? left : right
}

function canonicalIndexCell(left: IndexCell, right: IndexCell): IndexCell {
    const hashCmp = compareBytesLex(left.payloadHash, right.payloadHash)
    if (hashCmp !== 0) return hashCmp > 0 ? left : right
    const payloadCmp = compareBytesLex(left.payload, right.payload)
    return payloadCmp >= 0 ? left : right
}

function hashCellPayload(payload: Uint8Array): Uint8Array {
    return new Uint8Array(nobleSha256(payload))
}

interface ParsedTableSchema {
    columns: string[]
    autoIndexes: string[][]
}

interface ParsedIndexTerm {
    column: string
    desc: boolean
}

interface LogicalTableKeyInfo {
    keyOrdinals: number[][]
}

interface CollectedTableBtree {
    cells: BTreeCell[]
    pages: Set<number>
    cellsByPage: Map<number, BTreeCell[]>
}

type BtreeOwner = string
type SqliteMergeView = 'base' | 'local' | 'remote'

const SQLITE_MERGE_VIEWS = ['base', 'local', 'remote'] as const

function sqliteMasterEntryOwner(entry: SqliteMasterEntry): BtreeOwner {
    return `${entry.type}:${normalizeSqlIdentifier(entry.name)}`
}

function buildOwnerTableNameMap(entries: readonly SqliteMasterEntry[]): Map<BtreeOwner, string> {
    const ownerTableNames = new Map<BtreeOwner, string>()
    for (const entry of entries) {
        if (entry.rootPage <= 0) continue
        const tableName = entry.type === 'index' ? entry.tblName : entry.name
        ownerTableNames.set(sqliteMasterEntryOwner(entry), normalizeSqlIdentifier(tableName))
    }
    return ownerTableNames
}

function sqliteMasterEntryIdentity(entry: SqliteMasterEntry): string {
    return `${entry.type}\0${normalizeSqlIdentifier(entry.name)}`
}

function findPeerSqliteMasterEntry(
    entries: readonly SqliteMasterEntry[],
    target: SqliteMasterEntry,
): SqliteMasterEntry | null {
    const key = sqliteMasterEntryIdentity(target)
    return entries.find(entry => sqliteMasterEntryIdentity(entry) === key) ?? null
}

function buildLogicalTableKeyInfo(entry: SqliteMasterEntry | null | undefined): LogicalTableKeyInfo | null {
    if (!entry?.sql) return null
    const schema = parseCreateTableSchema(entry.sql)
    if (!schema || schema.autoIndexes.length === 0) return null
    const ordinalByName = new Map(schema.columns.map((column, ordinal) => [
        normalizeSqlIdentifier(column),
        ordinal,
    ]))
    const keyOrdinals = schema.autoIndexes
        .map(columns => columns.map(column => ordinalByName.get(normalizeSqlIdentifier(column))))
        .filter((ordinals): ordinals is number[] => (
            ordinals.length > 0
            && ordinals.every(ordinal => typeof ordinal === 'number')
        ))
    return keyOrdinals.length > 0 ? { keyOrdinals } : null
}

function tableCellLogicalKeySignatures(cell: BTreeCell, keyInfo: LogicalTableKeyInfo): string[] {
    const record = parseRecordForRewrite(cell.payload)
    const signatures: string[] = []
    for (const ordinals of keyInfo.keyOrdinals) {
        const parts: string[] = []
        let complete = true
        for (const ordinal of ordinals) {
            if (ordinal >= record.types.length) {
                complete = false
                break
            }
            const type = record.types[ordinal]
            if (type === 0n) {
                complete = false
                break
            }
            parts.push(`${type.toString()}:${bytesToHex(record.columns[ordinal])}`)
        }
        if (complete) signatures.push(parts.join('|'))
    }
    return signatures
}

function tableCellIndexProofSignatures(cell: BTreeCell, keyInfo: LogicalTableKeyInfo): string[] {
    return tableCellLogicalKeySignatures(cell, keyInfo)
        .map(signature => `${signature}\u0000${cell.rowId.toString()}`)
}

function tableCellsShareAnyLogicalKey(
    left: BTreeCell,
    right: BTreeCell,
    keyInfo: LogicalTableKeyInfo | null | undefined,
): boolean {
    if (!keyInfo) return false
    const leftKeys = new Set(tableCellLogicalKeySignatures(left, keyInfo))
    if (leftKeys.size === 0) return false
    return tableCellLogicalKeySignatures(right, keyInfo).some(key => leftKeys.has(key))
}

function dedupeTableCellsByLogicalKey(
    cells: BTreeCell[],
    keyInfo: LogicalTableKeyInfo | null | undefined,
): BTreeCell[] {
    if (!keyInfo || cells.length < 2) return cells

    const parent = cells.map((_, index) => index)
    const find = (index: number): number => {
        let current = index
        while (parent[current] !== current) {
            parent[current] = parent[parent[current]]
            current = parent[current]
        }
        return current
    }
    const union = (left: number, right: number): void => {
        const leftRoot = find(left)
        const rightRoot = find(right)
        if (leftRoot !== rightRoot) parent[rightRoot] = leftRoot
    }

    const bySignature = new Map<string, number>()
    for (let i = 0; i < cells.length; i += 1) {
        for (const signature of tableCellLogicalKeySignatures(cells[i], keyInfo)) {
            const existing = bySignature.get(signature)
            if (existing == null) {
                bySignature.set(signature, i)
            } else {
                union(existing, i)
            }
        }
    }

    const winners = new Map<number, BTreeCell>()
    for (let i = 0; i < cells.length; i += 1) {
        const root = find(i)
        const existing = winners.get(root)
        winners.set(root, existing ? canonicalCell(existing, cells[i]) : cells[i])
    }

    return [...winners.values()]
}

function mergeLeafCells(
    baseCells: BTreeCell[],
    localCells: BTreeCell[],
    remoteCells: BTreeCell[],
    allowPhysicalRowIdRebase = false,
    logicalKeyInfo?: LogicalTableKeyInfo | null,
): { success: true; mergedCells: BTreeCell[] } | { success: false } {
    const baseMap = tableCellsByRowId(baseCells)
    const localMap = tableCellsByRowId(localCells)
    const remoteMap = tableCellsByRowId(remoteCells)
    const mergedCellMap = new Map<bigint, BTreeCell>()

    for (const [rowId, cell] of baseMap) {
        mergedCellMap.set(rowId, cell)
    }

    for (const [rowId, localCell] of localMap) {
        const baseCell = baseMap.get(rowId)
        if (!baseCell) {
            mergedCellMap.set(rowId, localCell)
            continue
        }

        if (!uint8ArrayEqual(localCell.payloadHash, baseCell.payloadHash)) {
            mergedCellMap.set(rowId, localCell)
        }
    }

    for (const [rowId] of baseMap) {
        if (!localMap.has(rowId)) {
            mergedCellMap.delete(rowId)
        }
    }

    for (const [rowId, remoteCell] of remoteMap) {
        const baseCell = baseMap.get(rowId)
        if (!baseCell) {
            if (localMap.has(rowId)) {
                const localCell = localMap.get(rowId)!
                if (!uint8ArrayEqual(localCell.payloadHash, remoteCell.payloadHash)) {
                    if (!allowPhysicalRowIdRebase
                        || tableCellsShareAnyLogicalKey(localCell, remoteCell, logicalKeyInfo)) {
                        mergedCellMap.set(rowId, canonicalCell(localCell, remoteCell))
                        continue
                    }
                    const allRowIds = [...mergedCellMap.keys(), ...localMap.keys(), ...remoteMap.keys()]
                    const maxRowId = allRowIds.length > 0
                        ? allRowIds.reduce((left, right) => left > right ? left : right)
                        : 0n
                    const newRowId = maxRowId + 1n
                    mergedCellMap.set(rowId, remoteCell)
                    mergedCellMap.set(newRowId, {
                        rowId: newRowId,
                        payloadSize: localCell.payload.length,
                        payloadHash: localCell.payloadHash,
                        payload: localCell.payload,
                    })
                } else {
                    mergedCellMap.set(rowId, remoteCell)
                }
                continue
            }

            mergedCellMap.set(rowId, remoteCell)
            continue
        }

        if (!uint8ArrayEqual(remoteCell.payloadHash, baseCell.payloadHash)) {
            const localCell = localMap.get(rowId)
            if (localCell && !uint8ArrayEqual(localCell.payloadHash, baseCell.payloadHash)) {
                const mergedPayload = tryColumnMerge(
                    baseCell.payload,
                    localCell.payload,
                    remoteCell.payload,
                )
                if (mergedPayload == null) {
                    mergedCellMap.set(rowId, canonicalCell(localCell, remoteCell))
                    continue
                }

                mergedCellMap.set(rowId, {
                    rowId,
                    payloadSize: mergedPayload.length,
                    payloadHash: hashCellPayload(mergedPayload),
                    payload: mergedPayload,
                })
                continue
            }

            mergedCellMap.set(rowId, remoteCell)
        }
    }

    for (const [rowId] of baseMap) {
        if (!remoteMap.has(rowId)) {
            const localCell = localMap.get(rowId)
            if (localCell && !uint8ArrayEqual(localCell.payloadHash, baseMap.get(rowId)!.payloadHash)) {
                mergedCellMap.set(rowId, localCell)
                continue
            }
            mergedCellMap.delete(rowId)
        }
    }

    return {
        success: true,
        mergedCells: dedupeTableCellsByLogicalKey(
            [...mergedCellMap.values()],
            logicalKeyInfo,
        ).sort((left, right) => (
            left.rowId < right.rowId ? -1 : left.rowId > right.rowId ? 1 : 0
        )),
    }
}

function tableCellsByRowId(cells: BTreeCell[]): Map<bigint, BTreeCell> {
    const byRowId = new Map<bigint, BTreeCell>()
    for (const cell of cells) {
        const existing = byRowId.get(cell.rowId)
        byRowId.set(cell.rowId, existing ? canonicalCell(existing, cell) : cell)
    }
    return byRowId
}

function mergeIndexCellsRebasingRowIdCollisions(
    baseCells: IndexCell[],
    localCells: IndexCell[],
    remoteCells: IndexCell[],
    allowPhysicalRowIdRebase = false,
    uniqueKeyPrefix = false,
    uniqueKeyColumnCount: number | null = null,
): IndexCell[] {
    if (!allowPhysicalRowIdRebase) {
        return mergeIndexCellSetsDedupingUniqueKeys(
            baseCells,
            localCells,
            remoteCells,
            uniqueKeyPrefix,
            uniqueKeyColumnCount,
        )
    }

    const baseByRowId = indexCellsByLastRowId(baseCells)
    const remoteByRowId = indexCellsByLastRowId(remoteCells)
    const usedRowIds = new Set<bigint>()
    for (const cell of [...baseCells, ...localCells, ...remoteCells]) {
        const rowId = sqliteIndexPayloadLastRowId(cell.payload)
        if (rowId != null) usedRowIds.add(rowId)
    }

    const collisionRowIds = new Set<bigint>()
    for (const localCell of localCells) {
        const rowId = sqliteIndexPayloadLastRowId(localCell.payload)
        if (rowId == null) continue
        if (baseByRowId.has(rowId)) continue
        const remoteCell = remoteByRowId.get(rowId)
        if (!remoteCell || uint8ArrayEqual(localCell.payloadHash, remoteCell.payloadHash)) continue
        if (uniqueKeyPrefix && indexCellsShareKeyPrefix(localCell, remoteCell, uniqueKeyColumnCount)) continue

        collisionRowIds.add(rowId)
    }

    const remaps = new Map<bigint, bigint>()
    let next = usedRowIds.size > 0
        ? [...usedRowIds].reduce((left, right) => left > right ? left : right) + 1n
        : 1n
    for (const rowId of [...collisionRowIds].sort((left, right) => (
        left < right ? -1 : left > right ? 1 : 0
    ))) {
        while (usedRowIds.has(next)) next += 1n
        usedRowIds.add(next)
        remaps.set(rowId, next)
        next += 1n
    }

    if (remaps.size === 0) {
        return mergeIndexCellSetsDedupingUniqueKeys(
            baseCells,
            localCells,
            remoteCells,
            uniqueKeyPrefix,
            uniqueKeyColumnCount,
        )
    }

    return mergeIndexCellSetsDedupingUniqueKeys(
        baseCells,
        localCells.map(cell => remapIndexCellLastRowId(cell, remaps)),
        remoteCells,
        uniqueKeyPrefix,
        uniqueKeyColumnCount,
    )
}

function mergeIndexCellSetsDedupingUniqueKeys(
    baseCells: IndexCell[],
    localCells: IndexCell[],
    remoteCells: IndexCell[],
    uniqueKeyPrefix: boolean,
    uniqueKeyColumnCount: number | null = null,
): IndexCell[] {
    const merged = mergeIndexCellSets(baseCells, localCells, remoteCells)
    if ((!uniqueKeyPrefix && uniqueKeyColumnCount == null) || merged.length < 2) return merged

    const byPrefix = new Map<string, IndexCell>()
    for (const cell of merged) {
        const prefix = uniqueKeyColumnCount == null
            ? indexCellKeyPrefix(cell)
            : indexCellColumnPrefix(cell, uniqueKeyColumnCount)
        if (prefix == null) {
            byPrefix.set(`full:${bytesToHex(cell.payload)}`, cell)
            continue
        }
        const existing = byPrefix.get(prefix)
        byPrefix.set(prefix, existing ? canonicalIndexCell(existing, cell) : cell)
    }

    return [...byPrefix.values()].sort((a, b) => compareSqliteRecords(a.payload, b.payload))
}

function indexCellsShareKeyPrefix(
    left: IndexCell,
    right: IndexCell,
    columnCount: number | null = null,
): boolean {
    const leftPrefix = columnCount == null ? indexCellKeyPrefix(left) : indexCellColumnPrefix(left, columnCount)
    return leftPrefix != null && leftPrefix === (columnCount == null
        ? indexCellKeyPrefix(right)
        : indexCellColumnPrefix(right, columnCount))
}

function indexCellKeyPrefix(cell: IndexCell): string | null {
    const record = parseRecordForRewrite(cell.payload)
    if (record.types.length < 2) return null
    return indexCellRecordColumnPrefix(record, record.types.length - 1)
}

function indexCellColumnPrefix(cell: IndexCell, columnCount: number): string | null {
    const record = parseRecordForRewrite(cell.payload)
    if (record.types.length < columnCount || columnCount < 1) return null
    return indexCellRecordColumnPrefix(record, columnCount)
}

function indexCellRecordColumnPrefix(
    record: { types: bigint[]; columns: Uint8Array[] },
    columnCount: number,
): string {
    const parts: string[] = []
    for (let i = 0; i < columnCount; i += 1) {
        parts.push(`${record.types[i].toString()}:${bytesToHex(record.columns[i])}`)
    }
    return parts.join('|')
}

function indexCellIndexProofSignature(cell: IndexCell): string | null {
    const prefix = indexCellKeyPrefix(cell)
    const rowId = sqliteIndexPayloadLastRowId(cell.payload)
    return prefix != null && rowId != null ? `${prefix}\u0000${rowId.toString()}` : null
}

function indexCellsByLastRowId(cells: IndexCell[]): Map<bigint, IndexCell> {
    const byRowId = new Map<bigint, IndexCell>()
    for (const cell of cells) {
        const rowId = sqliteIndexPayloadLastRowId(cell.payload)
        if (rowId != null) byRowId.set(rowId, cell)
    }
    return byRowId
}

function remapIndexCellLastRowId(cell: IndexCell, remaps: ReadonlyMap<bigint, bigint>): IndexCell {
    const record = parseRecordForRewrite(cell.payload)
    if (record.types.length === 0) return cell
    const rowIdOrdinal = record.types.length - 1
    const oldRowId = decodeSqliteInteger(record.types[rowIdOrdinal], record.columns[rowIdOrdinal])
    if (oldRowId == null) return cell
    const newRowId = remaps.get(oldRowId)
    if (newRowId == null) return cell

    const encoded = encodeSqliteIntegerRecordColumn(newRowId)
    const types = record.types.slice()
    const columns = record.columns.slice()
    types[rowIdOrdinal] = encoded.type
    columns[rowIdOrdinal] = encoded.bytes
    const payload = rebuildSqliteRecord(types, columns)
    return {
        payloadSize: payload.length,
        payloadHash: hashCellPayload(payload),
        payload,
    }
}

function sqliteIndexPayloadLastRowId(payload: Uint8Array): bigint | null {
    const record = parseRecordForRewrite(payload)
    if (record.types.length === 0) return null
    const ordinal = record.types.length - 1
    return decodeSqliteInteger(record.types[ordinal], record.columns[ordinal])
}

function compareSqliteRecordsWithSortOrders(
    a: Uint8Array,
    b: Uint8Array,
    descendingColumns: readonly boolean[],
): number {
    if (descendingColumns.length === 0) return compareSqliteRecords(a, b)

    const aRecord = parseRecordForRewrite(a)
    const bRecord = parseRecordForRewrite(b)
    const nCols = Math.min(aRecord.types.length, bRecord.types.length)

    for (let i = 0; i < nCols; i += 1) {
        const aColumn = rebuildSqliteRecord([aRecord.types[i]], [aRecord.columns[i]])
        const bColumn = rebuildSqliteRecord([bRecord.types[i]], [bRecord.columns[i]])
        const rc = compareSqliteRecords(aColumn, bColumn)
        if (rc !== 0) return descendingColumns[i] ? -rc : rc
    }

    return aRecord.types.length - bRecord.types.length
}

function parseRecordForRewrite(payload: Uint8Array): { types: bigint[]; columns: Uint8Array[] } {
    const [headerSize, firstTypeOff] = readVarint(payload, 0)
    const hdrEnd = Number(headerSize)
    const types: bigint[] = []
    let off = firstTypeOff
    while (off < hdrEnd) {
        const [serialType, nextOff] = readVarint(payload, off)
        types.push(serialType)
        off = nextOff
    }

    const columns: Uint8Array[] = []
    let dataOff = hdrEnd
    for (const type of types) {
        const len = sqliteSerialTypeLength(type)
        columns.push(payload.slice(dataOff, dataOff + len))
        dataOff += len
    }
    return { types, columns }
}

function sqliteSerialTypeLength(type: bigint): number {
    const n = Number(type)
    if (n === 0) return 0
    if (n >= 1 && n <= 4) return n
    if (n === 5) return 6
    if (n === 6 || n === 7) return 8
    if (n === 8 || n === 9) return 0
    if (n >= 12 && n % 2 === 0) return (n - 12) / 2
    if (n >= 13 && n % 2 === 1) return (n - 13) / 2
    return 0
}

function bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

function decodeSqliteInteger(type: bigint, bytes: Uint8Array): bigint | null {
    const n = Number(type)
    if (n === 8) return 0n
    if (n === 9) return 1n
    if (n < 1 || n > 6) return null

    let value = 0n
    for (const byte of bytes) {
        value = (value << 8n) | BigInt(byte)
    }
    const bits = BigInt(bytes.length * 8)
    const signBit = 1n << (bits - 1n)
    return (value & signBit) === 0n ? value : value - (1n << bits)
}

function encodeSqliteIntegerRecordColumn(value: bigint): { type: bigint; bytes: Uint8Array } {
    if (value === 0n) return { type: 8n, bytes: new Uint8Array(0) }
    if (value === 1n) return { type: 9n, bytes: new Uint8Array(0) }

    const choices: Array<{ type: bigint; bytes: number }> = [
        { type: 1n, bytes: 1 },
        { type: 2n, bytes: 2 },
        { type: 3n, bytes: 3 },
        { type: 4n, bytes: 4 },
        { type: 5n, bytes: 6 },
        { type: 6n, bytes: 8 },
    ]
    for (const choice of choices) {
        const bits = BigInt(choice.bytes * 8)
        const min = -(1n << (bits - 1n))
        const max = (1n << (bits - 1n)) - 1n
        if (value < min || value > max) continue

        const bytes = new Uint8Array(choice.bytes)
        let encoded = value < 0n ? (1n << bits) + value : value
        for (let i = choice.bytes - 1; i >= 0; i -= 1) {
            bytes[i] = Number(encoded & 0xffn)
            encoded >>= 8n
        }
        return { type: choice.type, bytes }
    }

    throw new RangeError('SQLite rowid is outside signed int64 range')
}

function rebuildSqliteRecord(types: bigint[], columns: Uint8Array[]): Uint8Array {
    const typeVarints = types.map(type => writeVarint(type))
    const typeBytes = typeVarints.reduce((total, bytes) => total + bytes.length, 0)
    let headerSize = typeBytes + 1
    let headerSizeVarint = writeVarint(BigInt(headerSize))
    while (headerSizeVarint.length + typeBytes !== headerSize) {
        headerSize = headerSizeVarint.length + typeBytes
        headerSizeVarint = writeVarint(BigInt(headerSize))
    }

    const payloadSize = headerSize + columns.reduce((total, bytes) => total + bytes.length, 0)
    const result = new Uint8Array(payloadSize)
    let pos = 0
    result.set(headerSizeVarint, pos)
    pos += headerSizeVarint.length
    for (const typeVarint of typeVarints) {
        result.set(typeVarint, pos)
        pos += typeVarint.length
    }
    for (const column of columns) {
        result.set(column, pos)
        pos += column.length
    }
    return result
}

async function collectSqliteMasterEntries(
    pageSize: number,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
): Promise<SqliteMasterEntry[]> {
    const cache = new Map<number, Uint8Array>()

    async function load(pgno: number): Promise<Uint8Array | null> {
        const cached = cache.get(pgno)
        if (cached != null) return cached
        const page = await getPage(pgno as Pgno)
        if (page != null) cache.set(pgno, page)
        return page
    }

    async function walkSqliteMaster(pgno: number): Promise<void> {
        const page = await load(pgno)
        if (page == null) return
        const pageType = getPageType(page, pgno)
        if (pageType !== BTreePageType.TableInterior) return

        const hdrOff = pgno === 1 ? 100 : 0
        const view = new DataView(page.buffer, page.byteOffset)
        const cellCount = view.getUint16(hdrOff + 3)
        const rightmostChild = view.getUint32(hdrOff + 8)

        for (let i = 0; i < cellCount; i += 1) {
            const cellOff = view.getUint16(hdrOff + 12 + i * 2)
            const childPgno = view.getUint32(cellOff)
            if (childPgno > 0) await walkSqliteMaster(childPgno)
        }
        if (rightmostChild > 0) await walkSqliteMaster(rightmostChild)
    }

    await walkSqliteMaster(1)
    if (!cache.has(1)) await load(1)

    return parseSqliteMaster(pgno => cache.get(pgno) ?? null, pageSize).catch(() => [])
}

function mergeSqliteMasterEntries(
    entries: SqliteMasterEntry[][],
): SqliteMasterEntry[] {
    const byKey = new Map<string, SqliteMasterEntry>()
    for (const group of entries) {
        for (const entry of group) {
            byKey.set(`${entry.type}\0${entry.name}`, entry)
        }
    }
    return [...byKey.values()]
}

interface Fts5OpaqueGroup {
    name: string
    tableNames: string[]
}

interface Fts5RowIdParts {
    tombstone: boolean
    segId: number
    dlidx: boolean
    height: number
    pgno: number
}

interface Fts5StructureSegment {
    id: number
    first: number
    last: number
    origin1?: number
    origin2?: number
    tombstonePages?: number
}

interface Fts5StructureLevel {
    nMerge: number
    segments: Fts5StructureSegment[]
}

interface Fts5Structure {
    cookie: Uint8Array
    version2: boolean
    levels: Fts5StructureLevel[]
    writeCounter: number
    segments: Fts5StructureSegment[]
}

const FTS5_DATA_PAGE_B = 31n
const FTS5_DATA_HEIGHT_B = 5n
const FTS5_DATA_DLI_B = 1n
const FTS5_DATA_ID_B = 16n
const FTS5_DATA_PAGE_MASK = (1n << FTS5_DATA_PAGE_B) - 1n
const FTS5_DATA_HEIGHT_MASK = (1n << FTS5_DATA_HEIGHT_B) - 1n
const FTS5_DATA_ID_MASK = (1n << FTS5_DATA_ID_B) - 1n

function detectFts5OpaqueGroups(entries: SqliteMasterEntry[]): Fts5OpaqueGroup[] {
    const names = new Set(entries.map(entry => entry.name))
    const groups: Fts5OpaqueGroup[] = []
    for (const entry of entries) {
        if (entry.type !== 'table') continue
        if (!/^create\s+virtual\s+table\b/i.test(entry.sql ?? '')) continue
        if (!/\busing\s+fts5\s*\(/i.test(entry.sql ?? '')) continue
        const tableNames = [
            `${entry.name}_data`,
            `${entry.name}_idx`,
            `${entry.name}_content`,
            `${entry.name}_docsize`,
            `${entry.name}_config`,
        ].filter(name => names.has(name))
        if (tableNames.length > 0) groups.push({ name: entry.name, tableNames })
    }
    return groups
}

function decodeFts5DataRowId(rowId: bigint): Fts5RowIdParts | null {
    if (rowId <= 10n) return null
    let value = rowId
    const pgno = Number(value & FTS5_DATA_PAGE_MASK)
    value >>= FTS5_DATA_PAGE_B
    const height = Number(value & FTS5_DATA_HEIGHT_MASK)
    value >>= FTS5_DATA_HEIGHT_B
    const dlidx = (value & 1n) !== 0n
    value >>= FTS5_DATA_DLI_B
    const segId = Number(value & FTS5_DATA_ID_MASK)
    value >>= FTS5_DATA_ID_B
    const tombstone = (value & 1n) !== 0n
    if (segId <= 0) return null
    return { tombstone, segId, dlidx, height, pgno }
}

function encodeFts5DataRowId(parts: Fts5RowIdParts): bigint {
    let segId = BigInt(parts.segId)
    if (parts.tombstone) segId += 1n << FTS5_DATA_ID_B
    return (segId << (FTS5_DATA_PAGE_B + FTS5_DATA_HEIGHT_B + FTS5_DATA_DLI_B))
        | ((parts.dlidx ? 1n : 0n) << (FTS5_DATA_PAGE_B + FTS5_DATA_HEIGHT_B))
        | (BigInt(parts.height) << FTS5_DATA_PAGE_B)
        | BigInt(parts.pgno)
}

function extractFts5DataBlock(cell: BTreeCell | undefined): Uint8Array {
    if (!cell) return new Uint8Array(0)
    const record = parseRecordForRewrite(cell.payload)
    return record.columns[1] ?? new Uint8Array(0)
}

function buildFts5DataPayload(block: Uint8Array): Uint8Array {
    const blobType = BigInt(12 + block.length * 2)
    return rebuildSqliteRecord([0n, blobType], [new Uint8Array(0), block])
}

function makeTableCell(rowId: bigint, payload: Uint8Array): BTreeCell {
    return {
        rowId,
        payloadSize: payload.length,
        payloadHash: hashCellPayload(payload),
        payload,
    }
}

function parseFts5StructureRecord(block: Uint8Array): Fts5Structure | null {
    if (block.length < 4) return null
    let off = 4
    const cookie = block.slice(0, 4)
    let version2 = false
    if (block.length >= 8 && block[4] === 0xff && block[5] === 0x00 && block[6] === 0x00 && block[7] === 0x01) {
        version2 = true
        off = 8
    }
    try {
        const [nLevelRaw, off2] = readVarint(block, off); off = off2
        const [, off3] = readVarint(block, off); off = off3
        const [writeCounterRaw, off4] = readVarint(block, off); off = off4
        const levels: Fts5StructureLevel[] = []
        const segments: Fts5StructureSegment[] = []
        for (let i = 0; i < Number(nLevelRaw); i += 1) {
            const [nMergeRaw, mOff] = readVarint(block, off); off = mOff
            const [nSegRaw, sOff] = readVarint(block, off); off = sOff
            const levelSegments: Fts5StructureSegment[] = []
            for (let j = 0; j < Number(nSegRaw); j += 1) {
                const [idRaw, idOff] = readVarint(block, off); off = idOff
                const [firstRaw, firstOff] = readVarint(block, off); off = firstOff
                const [lastRaw, lastOff] = readVarint(block, off); off = lastOff
                const segment: Fts5StructureSegment = {
                    id: Number(idRaw),
                    first: Number(firstRaw),
                    last: Number(lastRaw),
                }
                if (version2) {
                    const [origin1Raw, o1] = readVarint(block, off); off = o1
                    const [origin2Raw, o2] = readVarint(block, off); off = o2
                    const [tombRaw, o3] = readVarint(block, off); off = o3
                    segment.origin1 = Number(origin1Raw)
                    segment.origin2 = Number(origin2Raw)
                    segment.tombstonePages = Number(tombRaw)
                }
                levelSegments.push(segment)
                segments.push(segment)
            }
            levels.push({ nMerge: Number(nMergeRaw), segments: levelSegments })
        }
        return { cookie, version2, levels, writeCounter: Number(writeCounterRaw), segments }
    } catch {
        return null
    }
}

function encodeFts5StructureRecord(structure: Fts5Structure): Uint8Array {
    const chunks: Uint8Array[] = [structure.cookie]
    if (structure.version2) chunks.push(new Uint8Array([0xff, 0x00, 0x00, 0x01]))
    const segmentCount = structure.levels.reduce((sum, level) => sum + level.segments.length, 0)
    chunks.push(writeVarint(BigInt(structure.levels.length)))
    chunks.push(writeVarint(BigInt(segmentCount)))
    chunks.push(writeVarint(BigInt(structure.writeCounter)))
    for (const level of structure.levels) {
        chunks.push(writeVarint(BigInt(level.nMerge)))
        chunks.push(writeVarint(BigInt(level.segments.length)))
        for (const segment of level.segments) {
            chunks.push(writeVarint(BigInt(segment.id)))
            chunks.push(writeVarint(BigInt(segment.first)))
            chunks.push(writeVarint(BigInt(segment.last)))
            if (structure.version2) {
                chunks.push(writeVarint(BigInt(segment.origin1 ?? 0)))
                chunks.push(writeVarint(BigInt(segment.origin2 ?? 0)))
                chunks.push(writeVarint(BigInt(segment.tombstonePages ?? 0)))
            }
        }
    }
    return concatBytes(chunks)
}

function mergeFts5Structures(
    remote: Fts5Structure,
    local: Fts5Structure,
    localSegIdMap: Map<number, number>,
): Fts5Structure {
    const levels = remote.levels.map(level => ({
        nMerge: level.nMerge,
        segments: level.segments.map(segment => ({ ...segment })),
    }))
    while (levels.length < local.levels.length) {
        levels.push({ nMerge: 0, segments: [] })
    }
    for (let i = 0; i < local.levels.length; i += 1) {
        for (const segment of local.levels[i].segments) {
            const mapped = localSegIdMap.get(segment.id)
            if (mapped == null) continue
            levels[i].segments.push({ ...segment, id: mapped })
        }
    }
    return {
        cookie: remote.cookie,
        version2: remote.version2,
        levels,
        writeCounter: Math.max(remote.writeCounter, local.writeCounter) + localSegIdMap.size,
        segments: levels.flatMap(level => level.segments),
    }
}

function mergeFts5Averages(base: Uint8Array, local: Uint8Array, remote: Uint8Array): Uint8Array {
    const baseVals = parseVarintList(base)
    const localVals = parseVarintList(local)
    const remoteVals = parseVarintList(remote)
    const width = Math.max(baseVals.length, localVals.length, remoteVals.length)
    const merged: bigint[] = []
    for (let i = 0; i < width; i += 1) {
        const value = (remoteVals[i] ?? 0n) + (localVals[i] ?? 0n) - (baseVals[i] ?? 0n)
        merged.push(value < 0n ? 0n : value)
    }
    return concatBytes(merged.map(value => writeVarint(value)))
}

function parseVarintList(block: Uint8Array): bigint[] {
    const values: bigint[] = []
    let off = 0
    while (off < block.length) {
        const [value, next] = readVarint(block, off)
        values.push(value)
        if (next <= off) break
        off = next
    }
    return values
}

function deriveConstantRowIdDelta(rowIdRemaps: ReadonlyMap<bigint, bigint> | undefined): bigint | null {
    if (rowIdRemaps == null || rowIdRemaps.size === 0) return 0n
    let delta: bigint | null = null
    for (const [from, to] of rowIdRemaps) {
        const nextDelta = to - from
        if (delta == null) delta = nextDelta
        else if (delta !== nextDelta) return null
    }
    return delta
}

function remapFts5LeafRowids(
    block: Uint8Array,
    rowIdRemaps: ReadonlyMap<bigint, bigint> | undefined,
): Uint8Array {
    const delta = deriveConstantRowIdDelta(rowIdRemaps)
    if (delta == null || delta === 0n || block.length < 4) return block
    const footerOff = (block[2] << 8) | block[3]
    if (footerOff < 4 || footerOff > block.length) return block
    const termOffsets: number[] = []
    let footerPos = footerOff
    let termOff = 0
    while (footerPos < block.length) {
        const [d, next] = readVarint(block, footerPos)
        if (next <= footerPos) throw new Error('SQLITE3MT_FTS5_BAD_PGIDX')
        termOff += Number(d)
        if (termOff >= 4 && termOff < footerOff) termOffsets.push(termOff)
        footerPos = next
    }
    const firstRowidOff = (block[0] << 8) | block[1]
    const rowidOffsets: number[] = []
    if (firstRowidOff >= 4 && firstRowidOff < footerOff && (termOffsets.length === 0 || firstRowidOff < termOffsets[0])) {
        rowidOffsets.push(firstRowidOff)
    }
    for (let i = 0; i < termOffsets.length; i += 1) {
        let off = termOffsets[i]
        const [a, off2] = readVarint(block, off)
        if (i === 0) {
            off = off2 + Number(a)
        } else {
            const [nNew, off3] = readVarint(block, off2)
            off = off3 + Number(nNew)
        }
        const end = termOffsets[i + 1] ?? footerOff
        if (off < end) rowidOffsets.push(off)
    }
    if (rowidOffsets.length === 0) return block

    const edits = rowidOffsets.map(off => buildVarintDeltaEdit(block, off, delta))
    const rebuiltLeaf = applyByteEdits(block.subarray(0, footerOff), edits)
    const remappedFooterOff = rebuiltLeaf.length
    if (remappedFooterOff > 0xffff) throw new Error('SQLITE3MT_FTS5_LEAF_TOO_LARGE')

    const remappedTermOffsets = termOffsets.map(off => off + byteEditShiftBefore(edits, off))
    const footerParts: Uint8Array[] = []
    let previous = 0
    for (const off of remappedTermOffsets) {
        footerParts.push(writeVarint(BigInt(off - previous)))
        previous = off
    }
    const footer = concatBytes(footerParts)
    const out = new Uint8Array(remappedFooterOff + footer.length)
    out.set(rebuiltLeaf, 0)
    out[2] = (remappedFooterOff >>> 8) & 0xff
    out[3] = remappedFooterOff & 0xff
    out.set(footer, remappedFooterOff)
    return out
}

function remapFts5DlidxRowids(
    block: Uint8Array,
    rowIdRemaps: ReadonlyMap<bigint, bigint> | undefined,
): Uint8Array {
    const delta = deriveConstantRowIdDelta(rowIdRemaps)
    if (delta == null || delta === 0n || block.length < 3) return block
    const [, pgnoEnd] = readVarint(block, 1)
    return applyByteEdits(block, [buildVarintDeltaEdit(block, pgnoEnd, delta)])
}

interface ByteEdit {
    off: number
    end: number
    replacement: Uint8Array
}

function buildVarintDeltaEdit(bytes: Uint8Array, off: number, delta: bigint): ByteEdit {
    const [oldValue, next] = readVarint(bytes, off)
    const encoded = writeVarint(oldValue + delta)
    return { off, end: next, replacement: encoded }
}

function applyByteEdits(bytes: Uint8Array, edits: ByteEdit[]): Uint8Array {
    if (edits.length === 0) return bytes.slice()
    const ordered = [...edits].sort((left, right) => left.off - right.off)
    let total = bytes.length
    let cursor = 0
    for (const edit of ordered) {
        if (edit.off < cursor || edit.off < 0 || edit.end > bytes.length || edit.end < edit.off) {
            throw new Error('SQLITE3MT_FTS5_OVERLAPPING_EDIT')
        }
        total += edit.replacement.length - (edit.end - edit.off)
        cursor = edit.end
    }
    const out = new Uint8Array(total)
    let readOff = 0
    let writeOff = 0
    for (const edit of ordered) {
        out.set(bytes.subarray(readOff, edit.off), writeOff)
        writeOff += edit.off - readOff
        out.set(edit.replacement, writeOff)
        writeOff += edit.replacement.length
        readOff = edit.end
    }
    out.set(bytes.subarray(readOff), writeOff)
    return out
}

function byteEditShiftBefore(edits: ByteEdit[], off: number): number {
    let shift = 0
    for (const edit of edits) {
        if (edit.off < off) shift += edit.replacement.length - (edit.end - edit.off)
    }
    return shift
}

function remapFts5IdxSegment(
    cell: IndexCell,
    segIdMap: ReadonlyMap<number, number>,
): IndexCell | null {
    const record = parseRecordForRewrite(cell.payload)
    if (record.types.length < 3) return null
    const oldSegId = decodeSqliteInteger(record.types[0], record.columns[0])
    if (oldSegId == null) return null
    const newSegId = segIdMap.get(Number(oldSegId))
    if (newSegId == null) return null
    const encoded = encodeSqliteIntegerRecordColumn(BigInt(newSegId))
    const types = record.types.slice()
    const columns = record.columns.slice()
    types[0] = encoded.type
    columns[0] = encoded.bytes
    const payload = rebuildSqliteRecord(types, columns)
    return {
        payloadSize: payload.length,
        payloadHash: hashCellPayload(payload),
        payload,
    }
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
    const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
    const out = new Uint8Array(total)
    let off = 0
    for (const chunk of chunks) {
        out.set(chunk, off)
        off += chunk.length
    }
    return out
}

function indexCellKey(cell: IndexCell): string {
    return bytesToHex(cell.payload)
}

async function collectBtreePagesWithOverflow(
    rootPgno: number,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
    pageSize: number,
): Promise<Set<number>> {
    const pages = new Set<number>()
    const stack = [rootPgno]

    while (stack.length > 0) {
        const pgno = stack.pop()!
        if (pgno <= 0 || pages.has(pgno)) continue
        const page = await getPage(pgno as Pgno)
        if (page == null) continue
        pages.add(pgno)

        const pageType = getPageType(page, pgno)
        if (pageType === BTreePageType.TableInterior || pageType === BTreePageType.IndexInterior) {
            const hdrOff = pgno === 1 ? 100 : 0
            const view = new DataView(page.buffer, page.byteOffset, page.byteLength)
            const cellCount = view.getUint16(hdrOff + 3)
            const rightmostChild = view.getUint32(hdrOff + 8)
            for (let i = 0; i < cellCount; i += 1) {
                const cellOff = view.getUint16(hdrOff + 12 + i * 2)
                const childPgno = view.getUint32(cellOff)
                if (childPgno > 0) stack.push(childPgno)
            }
            if (rightmostChild > 0) stack.push(rightmostChild)
        }

        for (const overflowPgno of await collectOverflowPagesFromBtreePage(page, pgno, pageType, getPage, pageSize)) {
            pages.add(overflowPgno)
        }
    }

    return pages
}

async function findOwningTableEntryForPage(
    entries: readonly SqliteMasterEntry[],
    pgno: Pgno,
    pageSize: number,
    getPageForView: (view: 'base' | 'local' | 'remote') => (pgno: Pgno) => Promise<Uint8Array | null>,
): Promise<SqliteMasterEntry | null> {
    const tables = entries.filter(entry => entry.type === 'table' && entry.rootPage > 0)
    const rootOwner = tables.find(entry => entry.rootPage === pgno)
    if (rootOwner != null) return rootOwner

    for (const entry of tables) {
        for (const view of ['local', 'remote', 'base'] as const) {
            const pages = await collectBtreePagesWithOverflow(entry.rootPage, getPageForView(view), pageSize)
            if (pages.has(pgno)) return entry
        }
    }

    return null
}

async function collectOverflowPagesFromBtreePage(
    page: Uint8Array,
    pgno: number,
    pageType: BTreePageType,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
    pageSize: number,
): Promise<number[]> {
    if (pageType !== BTreePageType.TableLeaf
        && pageType !== BTreePageType.IndexLeaf
        && pageType !== BTreePageType.IndexInterior) {
        return []
    }

    const hdrOff = pgno === 1 ? 100 : 0
    const view = new DataView(page.buffer, page.byteOffset, page.byteLength)
    const cellCount = view.getUint16(hdrOff + 3)
    const cellPtrStart = hdrOff + (pageType === BTreePageType.IndexInterior ? 12 : 8)
    const overflowPages: number[] = []

    for (let i = 0; i < cellCount; i += 1) {
        const cellOff = view.getUint16(cellPtrStart + i * 2)
        let payloadOff: number
        let payloadSize: number
        let maxLocal: number
        let minLocal: number

        if (pageType === BTreePageType.TableLeaf) {
            const [nPayload, off2] = readVarint(page, cellOff)
            const [, off3] = readVarint(page, off2)
            payloadOff = off3
            payloadSize = Number(nPayload)
            maxLocal = pageSize - 35
            minLocal = Math.floor(((pageSize - 12) * 32) / 255) - 23
        } else {
            const varintOff = pageType === BTreePageType.IndexInterior ? cellOff + 4 : cellOff
            const [nPayload, off2] = readVarint(page, varintOff)
            payloadOff = off2
            payloadSize = Number(nPayload)
            maxLocal = Math.floor(((pageSize - 12) * 64) / 255) - 23
            minLocal = Math.floor(((pageSize - 12) * 32) / 255) - 23
        }

        if (payloadSize <= maxLocal) continue
        let localBytes = minLocal + ((payloadSize - minLocal) % (pageSize - 4))
        if (localBytes > maxLocal) localBytes = minLocal
        let overflowPgno = view.getUint32(payloadOff + localBytes)
        let remaining = payloadSize - localBytes
        while (overflowPgno > 0 && remaining > 0) {
            overflowPages.push(overflowPgno)
            const overflowPage = await getPage(overflowPgno as Pgno)
            if (overflowPage == null) break
            const overflowView = new DataView(overflowPage.buffer, overflowPage.byteOffset, overflowPage.byteLength)
            overflowPgno = overflowView.getUint32(0)
            remaining -= pageSize - 4
        }
    }

    return overflowPages
}

function tableHasLogicalRowidTableKey(entry: SqliteMasterEntry): boolean {
    return !tableIsWithoutRowid(entry) && buildLogicalTableKeyInfo(entry) != null
}

function tableHasMergeableWithoutRowidKey(entry: SqliteMasterEntry): boolean {
    return tableIsWithoutRowid(entry) && withoutRowidPrimaryKeyColumnCount(entry) != null
}

function tableIsWithoutRowid(entry: SqliteMasterEntry): boolean {
    return /\bwithout\s+rowid\b/i.test(entry.sql ?? '')
}

function withoutRowidPrimaryKeyColumnCount(entry: SqliteMasterEntry | null | undefined): number | null {
    if (!entry?.sql || !tableIsWithoutRowid(entry)) return null
    const schema = parseCreateTableSchema(entry.sql)
    const primaryKey = schema?.autoIndexes[0]
    return primaryKey && primaryKey.length > 0 ? primaryKey.length : null
}

function buildIndexCellsFromTable(
    index: SqliteMasterEntry,
    table: SqliteMasterEntry,
    tableCells: BTreeCell[],
    compareRecords: (left: Uint8Array, right: Uint8Array) => number = compareSqliteRecords,
): IndexCell[] | null {
    if (!table.sql) return null
    const schema = parseCreateTableSchema(table.sql)
    if (!schema) return null
    const columnOrdinals = indexColumnOrdinals(index, table, schema)
    if (!columnOrdinals || columnOrdinals.length === 0) return null

    const indexCells: IndexCell[] = []
    for (const tableCell of tableCells) {
        const record = parseRecordForRewrite(tableCell.payload)
        const types: bigint[] = []
        const columns: Uint8Array[] = []
        for (const ordinal of columnOrdinals) {
            if (ordinal >= record.types.length) return null
            types.push(record.types[ordinal])
            columns.push(record.columns[ordinal])
        }
        const rowId = encodeSqliteIntegerRecordColumn(tableCell.rowId)
        types.push(rowId.type)
        columns.push(rowId.bytes)

        const payload = rebuildSqliteRecord(types, columns)
        indexCells.push({
            payloadSize: payload.length,
            payloadHash: hashCellPayload(payload),
            payload,
        })
    }

    return indexCells.sort((a, b) => compareRecords(a.payload, b.payload))
}

function buildIndexCellsFromWithoutRowidTable(
    index: SqliteMasterEntry,
    table: SqliteMasterEntry,
    tableCells: IndexCell[],
    compareRecords: (left: Uint8Array, right: Uint8Array) => number = compareSqliteRecords,
): IndexCell[] | null {
    if (!table.sql) return null
    const schema = parseCreateTableSchema(table.sql)
    if (!schema) return null
    const indexOrdinals = indexColumnOrdinals(index, table, schema)
    const primaryKeyColumns = schema.autoIndexes[0]
    if (!indexOrdinals || indexOrdinals.length === 0 || !primaryKeyColumns || primaryKeyColumns.length === 0) {
        return null
    }

    const columnIndexByName = new Map(schema.columns.map((name, i) => [normalizeSqlIdentifier(name), i]))
    const selectedOrdinals = [...indexOrdinals]
    for (const column of primaryKeyColumns) {
        const ordinal = columnIndexByName.get(normalizeSqlIdentifier(column))
        if (ordinal == null) return null
        if (!selectedOrdinals.includes(ordinal)) selectedOrdinals.push(ordinal)
    }

    const indexCells: IndexCell[] = []
    for (const tableCell of tableCells) {
        const record = parseRecordForRewrite(tableCell.payload)
        const types: bigint[] = []
        const columns: Uint8Array[] = []
        for (const ordinal of selectedOrdinals) {
            if (ordinal >= record.types.length) return null
            types.push(record.types[ordinal])
            columns.push(record.columns[ordinal])
        }

        const payload = rebuildSqliteRecord(types, columns)
        indexCells.push({
            payloadSize: payload.length,
            payloadHash: hashCellPayload(payload),
            payload,
        })
    }

    return indexCells.sort((a, b) => compareRecords(a.payload, b.payload))
}

function indexColumnOrdinals(
    index: SqliteMasterEntry,
    table: SqliteMasterEntry,
    schema: ParsedTableSchema,
): number[] | null {
    return indexColumnTerms(index, table, schema)?.map(term => term.ordinal) ?? null
}

function indexColumnTerms(
    index: SqliteMasterEntry,
    table: SqliteMasterEntry,
    schema: ParsedTableSchema,
): Array<{ ordinal: number; desc: boolean }> | null {
    const indexTerms = index.sql
        ? parseCreateIndexTerms(index.sql)
        : parseAutoIndexColumns(index.name, table.name, schema)?.map(column => ({ column, desc: false }))
    if (!indexTerms || indexTerms.length === 0) return null

    const columnIndexByName = new Map(schema.columns.map((name, i) => [normalizeSqlIdentifier(name), i]))
    const terms: Array<{ ordinal: number; desc: boolean }> = []
    for (const term of indexTerms) {
        const ordinal = columnIndexByName.get(normalizeSqlIdentifier(term.column))
        if (ordinal == null) return null
        terms.push({ ordinal, desc: term.desc })
    }
    return terms
}

function indexRecordComparator(
    index: SqliteMasterEntry,
    table: SqliteMasterEntry,
): ((left: Uint8Array, right: Uint8Array) => number) | null {
    if (!table.sql) return null
    const schema = parseCreateTableSchema(table.sql)
    if (!schema) return null
    const terms = indexColumnTerms(index, table, schema)
    if (terms == null) return null
    const descendingColumns = terms.map(term => term.desc)
    if (!descendingColumns.some(Boolean)) return compareSqliteRecords
    return (left, right) => compareSqliteRecordsWithSortOrders(left, right, descendingColumns)
}

function indexRecordComparatorForRoot(
    entries: readonly SqliteMasterEntry[],
    rootPage: number,
): ((left: Uint8Array, right: Uint8Array) => number) | null {
    const index = entries.find(entry => entry.type === 'index' && entry.rootPage === rootPage)
    if (!index) return null
    const table = entries.find(entry =>
        entry.type === 'table'
        && normalizeSqlIdentifier(entry.name) === normalizeSqlIdentifier(index.tblName))
    return table == null ? null : indexRecordComparator(index, table)
}

function indexRootAllowsPhysicalRowIdRebase(
    entries: readonly SqliteMasterEntry[],
    rootPage: number,
): boolean {
    const index = entries.find(entry => entry.type === 'index' && entry.rootPage === rootPage)
    if (!index) return false
    const parent = entries.find(entry =>
        entry.type === 'table'
        && entry.name === index.tblName)
    return parent != null && tableHasLogicalRowidTableKey(parent)
}

function indexRootHasUniqueKeyPrefix(
    entries: readonly SqliteMasterEntry[],
    rootPage: number,
): boolean {
    const index = entries.find(entry => entry.type === 'index' && entry.rootPage === rootPage)
    if (!index) return false
    return !index.sql || /\bcreate\s+unique\s+index\b/i.test(index.sql)
}

function parseCreateTableSchema(sql: string): ParsedTableSchema | null {
    const body = extractParenthesizedBody(sql)
    if (body == null) return null
    const definitions = splitSqlTopLevel(body, ',')
    const columns: string[] = []
    const autoIndexes: string[][] = []

    for (const definition of definitions) {
        const trimmed = definition.trim()
        if (trimmed.length === 0) continue
        let tokens = readLeadingSqlIdentifiers(trimmed)
        if (tokens.length === 0) continue
        let constraintSql = trimmed
        if (/^constraint$/i.test(tokens[0].text) && tokens.length >= 2) {
            constraintSql = trimmed.slice(tokens[0].raw.length + tokens[1].raw.length).trim()
            tokens = readLeadingSqlIdentifiers(constraintSql)
        }

        if (/^primary$/i.test(tokens[0]?.text ?? '') || /^unique$/i.test(tokens[0]?.text ?? '')) {
            const columnsFromConstraint = parseConstraintColumnList(constraintSql)
            if (columnsFromConstraint.length > 0) autoIndexes.push(columnsFromConstraint)
            continue
        }

        const columnName = tokens[0].text
        columns.push(columnName)
        const rest = trimmed.slice(tokens[0].raw.length)
        if (/\bprimary\s+key\b/i.test(rest) && !/^\s+integer\s+primary\s+key\b/i.test(rest)) {
            autoIndexes.push([columnName])
        }
        if (/\bunique\b/i.test(rest)) {
            autoIndexes.push([columnName])
        }
    }

    return { columns, autoIndexes }
}

function parseCreateIndexColumns(sql: string): string[] | null {
    return parseCreateIndexTerms(sql)?.map(term => term.column) ?? null
}

function parseCreateIndexTerms(sql: string): ParsedIndexTerm[] | null {
    const onMatch = /\bon\b/i.exec(sql)
    if (!onMatch) return null
    const body = extractParenthesizedBody(sql.slice(onMatch.index))
    if (body == null) return null
    const terms = splitSqlTopLevel(body, ',')
        .map(term => {
            const trimmed = term.trim()
            const identifier = readLeadingSqlIdentifiers(trimmed)[0]
            if (!identifier?.text) return null
            const rest = trimmed.slice(identifier.raw.length)
            return {
                column: identifier.text,
                desc: /\bdesc\b/i.test(rest),
            }
        })
        .filter((term): term is ParsedIndexTerm => term != null)
    return terms.length > 0 ? terms : null
}

function parseAutoIndexColumns(
    indexName: string,
    tableName: string,
    schema: ParsedTableSchema,
): string[] | null {
    const match = /^sqlite_autoindex_(.+)_(\d+)$/i.exec(indexName)
    if (!match) return null
    if (normalizeSqlIdentifier(match[1]) !== normalizeSqlIdentifier(tableName)) return null
    const ordinal = Number(match[2])
    if (!Number.isInteger(ordinal) || ordinal < 1) return null
    return schema.autoIndexes[ordinal - 1] ?? null
}

function parseConstraintColumnList(sql: string): string[] {
    const body = extractParenthesizedBody(sql)
    if (body == null) return []
    return splitSqlTopLevel(body, ',')
        .map(term => readLeadingSqlIdentifiers(term.trim())[0]?.text)
        .filter((name): name is string => typeof name === 'string' && name.length > 0)
}

function extractParenthesizedBody(sql: string): string | null {
    const start = sql.indexOf('(')
    if (start < 0) return null
    let depth = 0
    let quote: string | null = null
    for (let i = start; i < sql.length; i += 1) {
        const ch = sql[i]
        if (quote) {
            if (ch === quote) {
                if ((quote === '"' || quote === '\'' || quote === '`') && sql[i + 1] === quote) {
                    i += 1
                    continue
                }
                quote = null
            }
            continue
        }
        if (ch === '"' || ch === '\'' || ch === '`') {
            quote = ch
            continue
        }
        if (ch === '[') {
            quote = ']'
            continue
        }
        if (ch === '(') {
            depth += 1
            continue
        }
        if (ch === ')') {
            depth -= 1
            if (depth === 0) return sql.slice(start + 1, i)
        }
    }
    return null
}

function splitSqlTopLevel(sql: string, delimiter: string): string[] {
    const parts: string[] = []
    let start = 0
    let depth = 0
    let quote: string | null = null
    for (let i = 0; i < sql.length; i += 1) {
        const ch = sql[i]
        if (quote) {
            if (ch === quote) {
                if ((quote === '"' || quote === '\'' || quote === '`') && sql[i + 1] === quote) {
                    i += 1
                    continue
                }
                quote = null
            }
            continue
        }
        if (ch === '"' || ch === '\'' || ch === '`') {
            quote = ch
            continue
        }
        if (ch === '[') {
            quote = ']'
            continue
        }
        if (ch === '(') depth += 1
        else if (ch === ')') depth -= 1
        else if (ch === delimiter && depth === 0) {
            parts.push(sql.slice(start, i))
            start = i + 1
        }
    }
    parts.push(sql.slice(start))
    return parts
}

function readLeadingSqlIdentifiers(sql: string): Array<{ raw: string; text: string }> {
    const tokens: Array<{ raw: string; text: string }> = []
    let off = 0
    while (off < sql.length) {
        while (off < sql.length && /\s/.test(sql[off])) off += 1
        if (off >= sql.length) break
        const start = off
        const quote = sql[off]
        if (quote === '"' || quote === '`' || quote === '[') {
            const endQuote = quote === '[' ? ']' : quote
            off += 1
            let value = ''
            while (off < sql.length) {
                const ch = sql[off]
                if (ch === endQuote) {
                    if ((endQuote === '"' || endQuote === '`') && sql[off + 1] === endQuote) {
                        value += endQuote
                        off += 2
                        continue
                    }
                    off += 1
                    break
                }
                value += ch
                off += 1
            }
            tokens.push({ raw: sql.slice(start, off), text: value })
            continue
        }
        const match = /^[A-Za-z_][A-Za-z0-9_$]*/.exec(sql.slice(off))
        if (!match) break
        off += match[0].length
        tokens.push({ raw: sql.slice(start, off), text: match[0] })
    }
    return tokens
}

function normalizeSqlIdentifier(identifier: string): string {
    return identifier.toLowerCase()
}

interface SemanticMergeResult {
    pageHashes: Map<Pgno, PageHash>
    pageBytes: Map<Pgno, Uint8Array>
    resultPageCount: number
    affectedTableNames: Set<string>
    handledPages: Set<Pgno>
}

async function extractTableCellsWithOverflow(
    page: Uint8Array,
    pgno: Pgno,
    pageGetter: (pgno: Pgno) => Promise<Uint8Array | null>,
): Promise<BTreeCell[]> {
    const hdrOff = pgno === 1 ? 100 : 0
    const view = new DataView(page.buffer, page.byteOffset)

    if (page[hdrOff] !== BTreePageType.TableLeaf) {
        throw new Error(`extractTableCellsWithOverflow: not a table leaf page (type 0x${page[hdrOff].toString(16)})`)
    }

    const pageSize = page.length
    const maxLocal = pageSize - 35
    const minLocal = Math.floor(((pageSize - 12) * 32) / 255) - 23
    const overflowUsable = pageSize - 4
    const cellCount = view.getUint16(hdrOff + 3)
    const cellPtrStart = hdrOff + 8
    const cells: BTreeCell[] = []

    for (let i = 0; i < cellCount; i += 1) {
        const cellOff = view.getUint16(cellPtrStart + i * 2)
        const [nPayload, off2] = readVarint(page, cellOff)
        const [rowId, off3] = readVarint(page, off2)
        const payloadSize = Number(nPayload)
        let payload: Uint8Array

        if (payloadSize <= maxLocal) {
            payload = page.slice(off3, off3 + payloadSize)
        } else {
            let localBytes = minLocal + ((payloadSize - minLocal) % overflowUsable)
            if (localBytes > maxLocal) localBytes = minLocal

            payload = new Uint8Array(payloadSize)
            payload.set(page.subarray(off3, off3 + localBytes), 0)

            let overflowPgno = view.getUint32(off3 + localBytes)
            let written = localBytes
            while (overflowPgno > 0 && written < payloadSize) {
                const overflowPage = await pageGetter(overflowPgno as Pgno)
                if (overflowPage == null) {
                    throw new Error('MISSING_OVERFLOW_PAGE')
                }
                const overflowView = new DataView(overflowPage.buffer, overflowPage.byteOffset)
                const bytesToRead = Math.min(overflowUsable, payloadSize - written)
                payload.set(overflowPage.subarray(4, 4 + bytesToRead), written)
                written += bytesToRead
                overflowPgno = overflowView.getUint32(0)
            }
        }

        cells.push({
            rowId,
            payloadSize,
            payloadHash: hashCellPayload(payload),
            payload,
        })
    }

    return cells
}

async function collectTableCellsFromBtree(
    rootPgno: number,
    pageSize: number,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
): Promise<BTreeCell[] | null> {
    const collected = await collectTableBtree(rootPgno, pageSize, getPage)
    return collected?.cells ?? null
}

async function collectTableBtree(
    rootPgno: number,
    pageSize: number,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
): Promise<CollectedTableBtree | null> {
    const cells: BTreeCell[] = []
    const cellsByPage = new Map<number, BTreeCell[]>()
    const pages = await collectBtreePagesWithOverflow(rootPgno, getPage, pageSize)
    for (const pgno of pages) {
        const p = pgno as Pgno
        const page = await getPage(p)
        if (page == null) return null
        if (getPageType(page, pgno) !== BTreePageType.TableLeaf) continue
        try {
            const pageCells = await extractTableCellsWithOverflow(page, p, getPage)
            cellsByPage.set(pgno, pageCells)
            cells.push(...pageCells)
        } catch (error) {
            if (error instanceof Error && error.message === 'MISSING_OVERFLOW_PAGE') {
                return null
            }
            throw error
        }
    }
    cells.sort((left, right) => (
        left.rowId < right.rowId ? -1 : left.rowId > right.rowId ? 1 : 0
    ))
    return { cells, pages, cellsByPage }
}

async function collectIndexCellsFromBtree(
    rootPgno: number,
    pageSize: number,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
): Promise<IndexCell[] | null> {
    const pages = await collectBtreePagesWithOverflow(rootPgno, getPage, pageSize)
    const pageCache = new Map<number, Uint8Array>()
    for (const pgno of pages) {
        const page = await getPage(pgno as Pgno)
        if (page == null) return null
        pageCache.set(pgno, page)
    }
    const getCachedPage = (pgno: number): Uint8Array | null => pageCache.get(pgno) ?? null
    const cells: IndexCell[] = []
    for (const pgno of pages) {
        const page = pageCache.get(pgno)
        if (page == null) return null
        const pageType = getPageType(page, pgno)
        if (pageType === BTreePageType.IndexLeaf) {
            cells.push(...await extractIndexCells(page, pgno, getCachedPage, pageSize))
        } else if (pageType === BTreePageType.IndexInterior) {
            cells.push(...await extractInteriorIndexCells(page, pgno, getCachedPage, pageSize))
        }
    }
    return cells.sort((left, right) => compareSqliteRecords(left.payload, right.payload))
}

async function collectLogicalTableIndexProofs(
    table: SqliteMasterEntry,
    entries: readonly SqliteMasterEntry[],
    keyInfo: LogicalTableKeyInfo,
    pageSize: number,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
): Promise<Set<string> | null> {
    if (!table.sql) return null
    const schema = parseCreateTableSchema(table.sql)
    if (schema == null) return null
    const keyOrdinalSignatures = new Set(keyInfo.keyOrdinals.map(ordinals => ordinals.join(',')))
    const tableName = normalizeSqlIdentifier(table.name)

    for (const index of entries) {
        if (index.type !== 'index' || index.rootPage <= 0) continue
        if (normalizeSqlIdentifier(index.tblName) !== tableName) continue
        if (index.sql && !/\bcreate\s+unique\s+index\b/i.test(index.sql)) continue

        const ordinals = indexColumnOrdinals(index, table, schema)
        if (ordinals == null || !keyOrdinalSignatures.has(ordinals.join(','))) continue

        const indexCells = await collectIndexCellsFromBtree(index.rootPage, pageSize, getPage)
        if (indexCells == null) return null

        const proofs = new Set<string>()
        for (const indexCell of indexCells) {
            const proof = indexCellIndexProofSignature(indexCell)
            if (proof != null) proofs.add(proof)
        }
        return proofs
    }

    return null
}

async function collectValidatedLogicalTableBtree(
    table: SqliteMasterEntry,
    entries: readonly SqliteMasterEntry[],
    pageSize: number,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
): Promise<CollectedTableBtree | null> {
    const raw = await collectTableBtree(table.rootPage, pageSize, getPage)
    if (raw == null) return null

    const keyInfo = buildLogicalTableKeyInfo(table)
    if (keyInfo == null) return raw

    const proofs = await collectLogicalTableIndexProofs(table, entries, keyInfo, pageSize, getPage)
    if (proofs == null) return raw

    const invalidCells: BTreeCell[] = []
    for (const [pgno, pageCells] of raw.cellsByPage) {
        for (const cell of pageCells) {
            const proved = tableCellIndexProofSignatures(cell, keyInfo).some(proof => proofs.has(proof))
            if (!proved) invalidCells.push(cell)
        }
        if (invalidCells.length > 0) {
            if (logicalTableCellsHaveCoherentUniqueKeys(raw.cells, keyInfo)) {
                return raw
            }

            sdkConsole.warn('[sqlite3mt:merge-plan] logical table/index invariant failed', {
                table: table.name,
                page: pgno,
                invalidCellCount: invalidCells.length,
                tableCellCount: raw.cells.length,
                tablePageCount: raw.pages.size,
                keyOrdinals: keyInfo.keyOrdinals,
                invalidCells: invalidCells.slice(0, 8).map(cell => ({
                    rowId: cell.rowId.toString(),
                    payloadBytes: cell.payload.length,
                    logicalKeys: tableCellLogicalKeySignatures(cell, keyInfo),
                    indexProofs: tableCellIndexProofSignatures(cell, keyInfo),
                })),
                proofCount: proofs.size,
                proofSample: Array.from(proofs).slice(0, 8),
            })

            throw new Error(
                `SQLITE3MT_LOGICAL_TABLE_INDEX_INVARIANT: table ${table.name} page ${pgno} ` +
                `contains ${invalidCells.length} table cell(s) not proved by its logical unique index`,
            )
        }
    }

    return raw
}

function logicalTableCellsHaveCoherentUniqueKeys(
    cells: readonly BTreeCell[],
    keyInfo: LogicalTableKeyInfo,
): boolean {
    const signaturesSeen = new Set<string>()
    for (const cell of cells) {
        const signatures = Array.from(new Set(tableCellLogicalKeySignatures(cell, keyInfo)))
        if (signatures.length === 0) return false
        for (const signature of signatures) {
            if (signaturesSeen.has(signature)) {
                return false
            }
            signaturesSeen.add(signature)
        }
    }
    return true
}

async function collectBtreeOwnership(
    entries: readonly SqliteMasterEntry[],
    pageSize: number,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
): Promise<Map<number, Set<BtreeOwner>>> {
    const owners = new Map<number, Set<BtreeOwner>>()
    for (const entry of entries) {
        if (entry.rootPage <= 0) continue
        const owner = sqliteMasterEntryOwner(entry)
        for (const pgno of await collectBtreePagesWithOverflow(entry.rootPage, getPage, pageSize)) {
            let set = owners.get(pgno)
            if (set == null) {
                set = new Set()
                owners.set(pgno, set)
            }
            set.add(owner)
        }
    }
    return owners
}

function pageHasForeignOwner(
    ownershipByView: ReadonlyMap<'base' | 'local' | 'remote', ReadonlyMap<number, ReadonlySet<BtreeOwner>>>,
    pgno: number,
    owner: BtreeOwner,
): boolean {
    for (const owners of ownershipByView.values()) {
        const pageOwners = owners.get(pgno)
        if (pageOwners == null) continue
        for (const pageOwner of pageOwners) {
            if (pageOwner !== owner) return true
        }
    }
    return false
}

async function trySemanticLeafPageMerge(
    manifest: ManifestOperations,
    baseRoot: Root,
    remoteRoot: Root,
    localNorm: Map<Pgno, PageHash>,
    baseHashes: Map<Pgno, PageHash | null>,
    remoteHashes: Map<Pgno, PageHash | null>,
    pgno: Pgno,
    baseHash: PageHash | null,
    localHash: PageHash | null,
    remoteHash: PageHash | null,
    resultPageCount: number,
): Promise<SemanticMergeResult | null> {
    if (localHash == null || remoteHash == null || manifest.hashPage == null) {
        return null
    }

    async function pageHashForView(
        view: 'base' | 'local' | 'remote',
        targetPgno: Pgno,
    ): Promise<PageHash | null> {
        if (view === 'local') {
            if (localNorm.has(targetPgno)) return localNorm.get(targetPgno) ?? null
            return pageHashForView('base', targetPgno)
        }
        if (view === 'base') {
            if (baseHashes.has(targetPgno)) return baseHashes.get(targetPgno) ?? null
            const hash = await manifest.getPageHashFromConcreteRoot(baseRoot, targetPgno)
            baseHashes.set(targetPgno, hash)
            return hash
        }

        if (remoteHashes.has(targetPgno)) return remoteHashes.get(targetPgno) ?? null
        const hash = await manifest.getPageHashFromConcreteRoot(remoteRoot, targetPgno)
        remoteHashes.set(targetPgno, hash)
        return hash
    }

    async function pageBytesForView(
        view: 'base' | 'local' | 'remote',
        targetPgno: Pgno,
    ): Promise<Uint8Array | null> {
        const hash = await pageHashForView(view, targetPgno)
        return hash == null ? null : manifest.getPageBytesByHash(hash)
    }

    setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-fetch-local`)
    const localBytes = await manifest.getPageBytesByHash(localHash)
    setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-fetch-remote`)
    const remoteBytes = await manifest.getPageBytesByHash(remoteHash)
    setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-fetch-base`)
    const baseBytes = baseHash == null ? null : await manifest.getPageBytesByHash(baseHash)
    if (localBytes == null || remoteBytes == null || (baseHash != null && baseBytes == null)) {
        return null
    }

    setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-page-type`)
    const localType = getPageType(localBytes, pgno)
    const remoteType = getPageType(remoteBytes, pgno)
    const baseType = baseBytes == null ? localType : getPageType(baseBytes, pgno)
    if (localType !== remoteType || baseType !== localType) {
        return null
    }

    const pageHashes = new Map<Pgno, PageHash>()
    const pageBytes = new Map<Pgno, Uint8Array>()
    const affectedTableNames = new Set<string>()
    let sqliteMasterEntriesByView: Map<SqliteMergeView, SqliteMasterEntry[]> | null = null
    let sqliteMasterEntries: SqliteMasterEntry[] | null = null
    const getSqliteMasterEntriesByView = async (): Promise<Map<SqliteMergeView, SqliteMasterEntry[]>> => {
        if (sqliteMasterEntriesByView != null) return sqliteMasterEntriesByView
        const collected = await Promise.all(
            SQLITE_MERGE_VIEWS.map(view =>
                collectSqliteMasterEntries(
                    localBytes.length,
                    p => pageBytesForView(view, p),
                )),
        )
        sqliteMasterEntriesByView = new Map(
            SQLITE_MERGE_VIEWS.map((view, index) => [view, collected[index] ?? []]),
        )
        return sqliteMasterEntriesByView
    }
    const getSqliteMasterEntries = async (): Promise<SqliteMasterEntry[]> => {
        if (sqliteMasterEntries != null) return sqliteMasterEntries
        const byView = await getSqliteMasterEntriesByView()
        sqliteMasterEntries = mergeSqliteMasterEntries([...byView.values()])
        return sqliteMasterEntries
    }

    if (localType === BTreePageType.IndexLeaf) {
        setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-index-extract`)
        let baseCells: IndexCell[]
        let localCells: IndexCell[]
        let remoteCells: IndexCell[]
        try {
            baseCells = baseBytes == null
                ? []
                : await extractIndexCells(baseBytes, pgno, p => pageBytesForView('base', p as Pgno), localBytes.length)
            localCells = await extractIndexCells(localBytes, pgno, p => pageBytesForView('local', p as Pgno), localBytes.length)
            remoteCells = await extractIndexCells(remoteBytes, pgno, p => pageBytesForView('remote', p as Pgno), localBytes.length)
        } catch (error) {
            if (error instanceof Error && error.message === 'MISSING_OVERFLOW_PAGE') {
                return null
            }
            throw error
        }
        const entries = await getSqliteMasterEntries()
        const owningTable = await findOwningTableEntryForPage(
            entries,
            pgno,
            localBytes.length,
            view => p => pageBytesForView(view, p),
        )
        const allowPhysicalRowIdRebase = indexRootAllowsPhysicalRowIdRebase(entries, pgno)
        const uniqueKeyPrefix = indexRootHasUniqueKeyPrefix(entries, pgno)
        const uniqueKeyColumnCount = withoutRowidPrimaryKeyColumnCount(owningTable)
        const compareRecords = indexRecordComparatorForRoot(entries, pgno) ?? compareSqliteRecords
        const mergedCells = mergeIndexCellsRebasingRowIdCollisions(
            baseCells,
            localCells,
            remoteCells,
            allowPhysicalRowIdRebase,
            uniqueKeyPrefix,
            uniqueKeyColumnCount,
        )
        const rebuilt = buildIndexTreePages(pgno, mergedCells, localBytes.length, resultPageCount, [], compareRecords)
        resultPageCount = Math.max(resultPageCount, rebuilt.currentPageCount)
        for (const [pageNo, page] of rebuilt.pages) {
            const hash = manifest.hashPage(page)
            pageHashes.set(pageNo as Pgno, hash)
            pageBytes.set(pageNo as Pgno, page)
        }
        return {
            pageHashes,
            pageBytes,
            resultPageCount,
            affectedTableNames,
            handledPages: new Set([...rebuilt.pages.keys()].map(pageNo => pageNo as Pgno)),
        }
    }

    if (localType !== BTreePageType.TableLeaf) {
        return null
    }

    setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-extract-base`)
    let baseCells: BTreeCell[]
    let localCells: BTreeCell[]
    let remoteCells: BTreeCell[]
    try {
        baseCells = baseBytes == null
            ? []
            : await extractTableCellsWithOverflow(baseBytes, pgno, p => pageBytesForView('base', p))
        setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-extract-local`)
        localCells = await extractTableCellsWithOverflow(localBytes, pgno, p => pageBytesForView('local', p))
        setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-extract-remote`)
        remoteCells = await extractTableCellsWithOverflow(remoteBytes, pgno, p => pageBytesForView('remote', p))
    } catch (error) {
        if (error instanceof Error && error.message === 'MISSING_OVERFLOW_PAGE') {
            return null
        }
        throw error
    }

    setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-merge-cells`)
    const entries = await getSqliteMasterEntries()
    const entriesByView = await getSqliteMasterEntriesByView()
    const entriesForView = (view: SqliteMergeView): readonly SqliteMasterEntry[] =>
        entriesByView.get(view) ?? []
    const entryForView = (
        entry: SqliteMasterEntry,
        view: SqliteMergeView,
    ): SqliteMasterEntry | null => findPeerSqliteMasterEntry(entriesForView(view), entry)
    const ownershipByView = new Map<'base' | 'local' | 'remote', Map<number, Set<BtreeOwner>>>()
    for (const view of ['base', 'local', 'remote'] as const) {
        ownershipByView.set(view, await collectBtreeOwnership(
            entriesForView(view),
            localBytes.length,
            p => pageBytesForView(view, p),
        ))
    }
    const rootPages = new Set<number>([1])
    for (const viewEntries of entriesByView.values()) {
        for (const entry of viewEntries) {
            if (entry.type === 'table' && entry.rootPage > 0) {
                rootPages.add(entry.rootPage)
            }
        }
    }
    const tableEntry = await findOwningTableEntryForPage(
        entries,
        pgno,
        localBytes.length,
        view => p => pageBytesForView(view, p),
    )
    const logicalKeyInfo = buildLogicalTableKeyInfo(tableEntry)
    const allowPhysicalRowIdRebase = logicalKeyInfo != null
    if (tableEntry != null && logicalKeyInfo != null && tableEntry.rootPage > 0) {
        setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-table-rebuild`)
        const collectViewTable = (
            view: SqliteMergeView,
        ): Promise<CollectedTableBtree | null> => {
            const viewEntry = entryForView(tableEntry, view)
            if (viewEntry == null || viewEntry.rootPage <= 0) {
                return Promise.resolve({ cells: [], pages: new Set(), cellsByPage: new Map() })
            }
            return collectValidatedLogicalTableBtree(
                viewEntry,
                entriesForView(view),
                localBytes.length,
                p => pageBytesForView(view, p),
            )
        }
        const [baseTable, localTable, remoteTable] = await Promise.all([
            collectViewTable('base'),
            collectViewTable('local'),
            collectViewTable('remote'),
        ])
        if (baseTable != null && localTable != null && remoteTable != null) {
            const tableMerge = mergeLeafCells(
                baseTable.cells,
                localTable.cells,
                remoteTable.cells,
                true,
                logicalKeyInfo,
            )
            if (tableMerge.success) {
                const owner = sqliteMasterEntryOwner(tableEntry)
                const previousPages = new Set<number>([
                    ...baseTable.pages,
                    ...localTable.pages,
                    ...remoteTable.pages,
                ].filter(previousPgno =>
                    !pageHasForeignOwner(ownershipByView, previousPgno, owner)))
                const rootTemplate = tableEntry.rootPage === 1
                    ? await pageBytesForView('local', 1 as Pgno) ?? undefined
                    : undefined
                const rebuilt = buildTableTreePages(
                    tableEntry.rootPage,
                    tableMerge.mergedCells,
                    localBytes.length,
                    resultPageCount,
                    rootTemplate,
                )
                const zeroPage = new Uint8Array(localBytes.length)
                const zeroHash = manifest.hashPage(zeroPage)
                const writtenPages = new Set(rebuilt.pages.keys())
                for (const previousPgno of previousPages) {
                    if (previousPgno <= 1 || writtenPages.has(previousPgno)) continue
                    const p = previousPgno as Pgno
                    pageHashes.set(p, zeroHash)
                    pageBytes.set(p, zeroPage)
                }
                for (const [rebuiltPgno, rebuiltPage] of rebuilt.pages) {
                    const p = rebuiltPgno as Pgno
                    pageHashes.set(p, manifest.hashPage(rebuiltPage))
                    pageBytes.set(p, rebuiltPage)
                }
                affectedTableNames.add(normalizeSqlIdentifier(tableEntry.name))
                const handledPages = new Set<Pgno>()
                for (const previousPgno of previousPages) handledPages.add(previousPgno as Pgno)
                for (const rebuiltPgno of rebuilt.pages.keys()) handledPages.add(rebuiltPgno as Pgno)
                return {
                    pageHashes,
                    pageBytes,
                    resultPageCount: rebuilt.currentPageCount,
                    affectedTableNames,
                    handledPages,
                }
            }
        }
    }
    const merged = mergeLeafCells(
        baseCells,
        localCells,
        remoteCells,
        allowPhysicalRowIdRebase,
        logicalKeyInfo,
    )
    if (!merged.success) {
        return null
    }

    setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-build-page`)
    let nextResultPageCount = resultPageCount
    const needsOverflow = merged.mergedCells.some(cell => cell.payloadSize > localBytes.length - 35)
    let mergedPage: Uint8Array | null = null
    if (needsOverflow) {
        const overflowResult = buildLeafPageWithOverflow(
            merged.mergedCells,
            localBytes.length,
            pgno,
            resultPageCount,
            pgno === 1 ? localBytes : undefined,
        )
        if (overflowResult == null) {
            return null
        }
        mergedPage = overflowResult.leafPage
        nextResultPageCount = overflowResult.newPageCount
        for (const overflowPage of overflowResult.overflowPages) {
            const overflowHash = manifest.hashPage(overflowPage.page)
            pageHashes.set(overflowPage.pgno as Pgno, overflowHash)
            pageBytes.set(overflowPage.pgno as Pgno, overflowPage.page)
        }
    } else {
        mergedPage = buildLeafPage(
            merged.mergedCells,
            localBytes.length,
            pgno,
            pgno === 1 ? localBytes : undefined,
        )
    }
    if (mergedPage == null) {
        if (!rootPages.has(pgno)) {
            return null
        }
        if (needsOverflow) {
            const split = splitPagesWithOverflow(
                merged.mergedCells,
                localBytes.length,
                pgno,
                resultPageCount,
                pgno === 1 ? localBytes : undefined,
            )
            mergedPage = split.interiorPage.page
            nextResultPageCount = split.newPageCount
            for (const leafPage of split.leafPages) {
                const hash = manifest.hashPage(leafPage.page)
                pageHashes.set(leafPage.pgno as Pgno, hash)
                pageBytes.set(leafPage.pgno as Pgno, leafPage.page)
            }
            for (const overflowPage of split.overflowPages) {
                const hash = manifest.hashPage(overflowPage.page)
                pageHashes.set(overflowPage.pgno as Pgno, hash)
                pageBytes.set(overflowPage.pgno as Pgno, overflowPage.page)
            }
        } else {
            const split = splitPages(
                merged.mergedCells,
                localBytes.length,
                'table',
                resultPageCount,
                [],
                pgno,
            )
            mergedPage = split.interiorPage.page
            nextResultPageCount = split.newPageCount
            for (const leafPage of split.leafPages) {
                const hash = manifest.hashPage(leafPage.page)
                pageHashes.set(leafPage.pgno as Pgno, hash)
                pageBytes.set(leafPage.pgno as Pgno, leafPage.page)
            }
        }
    }

    if (pgno === 1) {
        setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-merge-header`)
        if (!mergePage1Header(mergedPage, localBytes, remoteBytes)) {
            return null
        }
        new DataView(mergedPage.buffer, mergedPage.byteOffset).setUint32(28, nextResultPageCount)
    }

    setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-hash-page`)
    pageHashes.set(pgno, manifest.hashPage(mergedPage))
    pageBytes.set(pgno, mergedPage)
    if (tableEntry != null && logicalKeyInfo != null) {
        affectedTableNames.add(normalizeSqlIdentifier(tableEntry.name))
    }
    return {
        pageHashes,
        pageBytes,
        resultPageCount: nextResultPageCount,
        affectedTableNames,
        handledPages: new Set(pageHashes.keys()),
    }
}

function canonicalHash(left: PageHash | null, right: PageHash | null): PageHash | null {
    if (left == null) return right
    if (right == null) return left
    return left >= right ? left : right
}

function buildFreelistPages(
    freePages: readonly number[],
    pageSize: number,
): { firstTrunkPgno: number; totalFreePages: number; pages: Map<number, Uint8Array> } | null {
    const sorted = [...new Set(freePages.filter(pgno => pgno > 1))].sort((a, b) => a - b)
    if (sorted.length === 0) return null

    const maxLeafEntries = Math.floor(pageSize / 4) - 2
    const pages = new Map<number, Uint8Array>()
    const trunks: Array<{ pgno: number; leaves: number[] }> = []
    let off = 0
    while (off < sorted.length) {
        const pgno = sorted[off++]
        const leaves = sorted.slice(off, off + maxLeafEntries)
        off += leaves.length
        trunks.push({ pgno, leaves })
    }

    for (let i = 0; i < trunks.length; i += 1) {
        const trunk = new Uint8Array(pageSize)
        const view = new DataView(trunk.buffer, trunk.byteOffset, trunk.byteLength)
        view.setUint32(0, trunks[i + 1]?.pgno ?? 0)
        view.setUint32(4, trunks[i].leaves.length)
        trunks[i].leaves.forEach((leafPgno, leafIndex) => {
            view.setUint32(8 + leafIndex * 4, leafPgno)
        })
        pages.set(trunks[i].pgno, trunk)
    }

    return {
        firstTrunkPgno: trunks[0].pgno,
        totalFreePages: sorted.length,
        pages,
    }
}

export function removeReachablePagesFromFreeSet(
    freePages: Set<number>,
    reachablePages: Iterable<number>,
    resultPageCount: number,
): void {
    for (const pgno of [...freePages]) {
        if (pgno <= 1 || pgno > resultPageCount) freePages.delete(pgno)
    }
    for (const pgno of reachablePages) {
        freePages.delete(pgno)
    }
}

async function collectFreelistPageNumbers(
    page1: Uint8Array | null,
    getPage: (pgno: Pgno) => Promise<Uint8Array | null>,
    pageSize: number,
    pageCount: number,
): Promise<Set<number>> {
    const pages = new Set<number>()
    if (page1 == null || page1.byteLength < 40 || pageCount <= 1) return pages

    const view = new DataView(page1.buffer, page1.byteOffset, page1.byteLength)
    let trunkPgno = view.getUint32(32)
    const seenTrunks = new Set<number>()
    while (trunkPgno > 1 && trunkPgno <= pageCount && !seenTrunks.has(trunkPgno)) {
        seenTrunks.add(trunkPgno)
        pages.add(trunkPgno)

        const trunk = await getPage(trunkPgno as Pgno)
        if (trunk == null || trunk.byteLength < 8) break

        const trunkView = new DataView(trunk.buffer, trunk.byteOffset, trunk.byteLength)
        const nextTrunk = trunkView.getUint32(0)
        const leafCount = Math.min(trunkView.getUint32(4), Math.floor(pageSize / 4) - 2)
        for (let index = 0; index < leafCount; index += 1) {
            const leafPgno = trunkView.getUint32(8 + index * 4)
            if (leafPgno > 1 && leafPgno <= pageCount) pages.add(leafPgno)
        }
        trunkPgno = nextTrunk
    }

    return pages
}

function buildTableTreePages(
    rootPgno: number,
    cells: BTreeCell[],
    pageSize: number,
    currentPageCount: number,
    rootTemplate?: Uint8Array,
    reusablePages: number[] = [],
): { pages: Map<number, Uint8Array>; currentPageCount: number } {
    const pages = new Map<number, Uint8Array>()
    const sorted = [...cells].sort((left, right) => (
        left.rowId < right.rowId ? -1 : left.rowId > right.rowId ? 1 : 0
    ))
    const needsOverflow = sorted.some(cell => cell.payloadSize > pageSize - 35)
    if (needsOverflow) {
        const single = buildLeafPageWithOverflow(sorted, pageSize, rootPgno, currentPageCount, rootTemplate)
        if (single != null) {
            pages.set(rootPgno, single.leafPage)
            for (const overflowPage of single.overflowPages) pages.set(overflowPage.pgno, overflowPage.page)
            return { pages, currentPageCount: single.newPageCount }
        }

        const split = splitPagesWithOverflow(sorted, pageSize, rootPgno, currentPageCount, rootTemplate)
        pages.set(split.interiorPage.pgno, split.interiorPage.page)
        for (const interiorPage of split.interiorPages) pages.set(interiorPage.pgno, interiorPage.page)
        for (const leafPage of split.leafPages) pages.set(leafPage.pgno, leafPage.page)
        for (const overflowPage of split.overflowPages) pages.set(overflowPage.pgno, overflowPage.page)
        return { pages, currentPageCount: split.newPageCount }
    }

    const single = buildLeafPage(sorted, pageSize, rootPgno, rootTemplate)
    if (single != null) {
        pages.set(rootPgno, single)
        return { pages, currentPageCount }
    }

    const split = splitPages(sorted, pageSize, 'table', currentPageCount, reusablePages, rootPgno)
    pages.set(split.interiorPage.pgno, split.interiorPage.page)
    for (const interiorPage of split.interiorPages) pages.set(interiorPage.pgno, interiorPage.page)
    for (const leafPage of split.leafPages) pages.set(leafPage.pgno, leafPage.page)
    return { pages, currentPageCount: split.newPageCount }
}

function buildIndexTreePages(
    rootPgno: number,
    cells: IndexCell[],
    pageSize: number,
    currentPageCount: number,
    reusablePages: number[] = [],
    compareRecords: (left: Uint8Array, right: Uint8Array) => number = compareSqliteRecords,
): { pages: Map<number, Uint8Array>; currentPageCount: number } {
    const pages = new Map<number, Uint8Array>()
    const sorted = [...cells].sort((left, right) => compareRecords(left.payload, right.payload))
    const needsOverflow = sorted.some(cell => cell.payloadSize > Math.floor(((pageSize - 12) * 64) / 255) - 23)
    if (needsOverflow) {
        const single = buildIndexLeafPageWithOverflow(sorted, pageSize, rootPgno, currentPageCount)
        if (single != null) {
            pages.set(rootPgno, single.leafPage)
            for (const overflowPage of single.overflowPages) pages.set(overflowPage.pgno, overflowPage.page)
            return { pages, currentPageCount: single.newPageCount }
        }

        const split = splitIndexPagesWithOverflow(sorted, pageSize, rootPgno, currentPageCount, reusablePages)
        pages.set(split.interiorPage.pgno, split.interiorPage.page)
        for (const interiorPage of split.interiorPages) pages.set(interiorPage.pgno, interiorPage.page)
        for (const leafPage of split.leafPages) pages.set(leafPage.pgno, leafPage.page)
        for (const overflowPage of split.overflowPages) pages.set(overflowPage.pgno, overflowPage.page)
        return { pages, currentPageCount: split.newPageCount }
    }

    const single = buildIndexLeafPage(sorted, pageSize, rootPgno)
    if (single != null) {
        pages.set(rootPgno, single)
        return { pages, currentPageCount }
    }

    const split = splitIndexPagesWithOverflow(sorted, pageSize, rootPgno, currentPageCount, reusablePages)
    pages.set(split.interiorPage.pgno, split.interiorPage.page)
    for (const interiorPage of split.interiorPages) pages.set(interiorPage.pgno, interiorPage.page)
    for (const leafPage of split.leafPages) pages.set(leafPage.pgno, leafPage.page)
    for (const overflowPage of split.overflowPages) pages.set(overflowPage.pgno, overflowPage.page)
    return { pages, currentPageCount: split.newPageCount }
}

/**
 * Build the three-way merge plan for remote reconcile.
 *
 * §15.3 normative requirements:
 * 1. remoteChanged = diffConcreteRoots(baseRoot, remoteRoot)
 * 2. liveLocalPageCount starts as workspacePageCount
 * 3. resultPageCount resolved via resolveMergedPageCount (not from page-1)
 * 4. Concurrent EOF changes resolve conservatively to the largest reachable
 *    page count, preserving tail pages until a later SQLite/manifest compaction
 *    can prove they are unreachable.
 * 5. importDelta.postPageCount from plan's resolved resultPageCount
 *
 * Per-page three-way merge:
 * - localHash === baseHash → remote wins (import remote bytes)
 * - localHash === remoteHash → same change (no import needed)
 * - remoteHash === baseHash → only local changed (no import)
 * - all three differ → semantic merge; irreducible cases use a canonical
 *   content-hash winner so the result is independent of local/remote vantage
 */
export async function buildMergePlan(
    manifest: ManifestOperations,
    args: BuildMergePlanArgs,
): Promise<MergePlan> {
    const {
        baseRoot,
        baseVersion,
        remoteRoot,
        remoteVersion,
        wal,
        localNorm,
        localPageCount,
        pendingDbDeltasIntegratedFrameMax,
    } = args

    // §15.3 step 1: diff base vs remote
    setMergePlanDebugStage(manifest, 'build-merge-plan:diff-roots')
    const remoteChanged = await manifest.diffConcreteRoots(baseRoot, remoteRoot)

    // §15.3 step 2–4: resolve page counts via the shared canonical helper
    const basePageCount = baseRoot.meta.pageCount
    const remotePageCount = remoteRoot.meta.pageCount
    let resultPageCount = resolveMergedPageCount(
        basePageCount,
        localPageCount,
        remotePageCount,
    )

    // §15.3 step 5: three-way merge for each remoteChanged page
    const remoteHashes = new Map<Pgno, PageHash | null>()
    const baseHashes = new Map<Pgno, PageHash | null>()
    const resultHashByPgno = new Map<Pgno, PageHash>()
    const importBlobsByPgno = new Map<Pgno, Uint8Array>()
    const importBlobsByHash = new Map<PageHash, Uint8Array>()
    const basePagesCache = new Map<Pgno, Uint8Array>()
    const remotePagesCache = new Map<Pgno, Uint8Array>()
    const handledOpaquePages = new Set<Pgno>()
    const freePages = new Set<number>()
    const semanticallyMergedLogicalTables = new Set<string>()

    async function pageHashForView(
        view: 'base' | 'local' | 'remote',
        targetPgno: Pgno,
    ): Promise<PageHash | null> {
        if (view === 'local') {
            if (localNorm.has(targetPgno)) return localNorm.get(targetPgno) ?? null
            return pageHashForView('base', targetPgno)
        }
        if (view === 'base') {
            if (baseHashes.has(targetPgno)) return baseHashes.get(targetPgno) ?? null
            const hash = await manifest.getPageHashFromConcreteRoot(baseRoot, targetPgno)
            baseHashes.set(targetPgno, hash)
            return hash
        }

        if (remoteHashes.has(targetPgno)) return remoteHashes.get(targetPgno) ?? null
        const hash = await manifest.getPageHashFromConcreteRoot(remoteRoot, targetPgno)
        remoteHashes.set(targetPgno, hash)
        return hash
    }

    async function pageBytesForView(
        view: 'base' | 'local' | 'remote',
        targetPgno: Pgno,
    ): Promise<Uint8Array | null> {
        const hash = await pageHashForView(view, targetPgno)
        return hash == null ? null : manifest.getPageBytesByHash(hash)
    }

    const page1ForMaster = await pageBytesForView('local', 1 as Pgno)
        ?? await pageBytesForView('remote', 1 as Pgno)
        ?? await pageBytesForView('base', 1 as Pgno)
    const pageSize = page1ForMaster?.byteLength ?? 4096

    setMergePlanDebugStage(manifest, 'build-merge-plan:fts5-opaque-groups')
    const collectedMasterEntries = await Promise.all(
        SQLITE_MERGE_VIEWS.map(view =>
            collectSqliteMasterEntries(pageSize, p => pageBytesForView(view, p))),
    )
    const masterEntriesByView = new Map<SqliteMergeView, SqliteMasterEntry[]>(
        SQLITE_MERGE_VIEWS.map((view, index) => [view, collectedMasterEntries[index] ?? []]),
    )
    const masterEntries = mergeSqliteMasterEntries(collectedMasterEntries)
    const ownerTableNames = buildOwnerTableNameMap(masterEntries)
    const entriesForView = (view: SqliteMergeView): readonly SqliteMasterEntry[] =>
        masterEntriesByView.get(view) ?? []
    const entryForView = (
        entry: SqliteMasterEntry,
        view: SqliteMergeView,
    ): SqliteMasterEntry | null => findPeerSqliteMasterEntry(entriesForView(view), entry)
    const entriesByName = new Map(masterEntries.map(entry => [entry.name, entry]))
    const entriesByNameByView = new Map<SqliteMergeView, Map<string, SqliteMasterEntry>>(
        SQLITE_MERGE_VIEWS.map(view => [
            view,
            new Map(entriesForView(view).map(entry => [entry.name, entry])),
        ]),
    )
    const entryByNameForView = (
        view: SqliteMergeView,
        name: string,
    ): SqliteMasterEntry | null => entriesByNameByView.get(view)?.get(name) ?? null
    const fts5Groups = detectFts5OpaqueGroups(masterEntries)
    const fts5ShadowTableNames = new Set(fts5Groups.flatMap(group => group.tableNames))
    for (const group of fts5Groups) {
        const pagesByView = new Map<'base' | 'local' | 'remote', Set<number>>()
        for (const view of ['base', 'local', 'remote'] as const) {
            const rootPages = group.tableNames
                .map(name => entryByNameForView(view, name)?.rootPage ?? 0)
                .filter(rootPage => rootPage > 0)
            if (rootPages.length === 0) {
                pagesByView.set(view, new Set())
                continue
            }
            const pages = new Set<number>()
            for (const rootPage of rootPages) {
                for (const pgno of await collectBtreePagesWithOverflow(
                    rootPage,
                    p => pageBytesForView(view, p),
                    pageSize,
                )) {
                    pages.add(pgno)
                }
            }
            pagesByView.set(view, pages)
        }

        const allPages = new Set<number>([
            ...(pagesByView.get('base') ?? []),
            ...(pagesByView.get('local') ?? []),
            ...(pagesByView.get('remote') ?? []),
        ])
        let localTouched = false
        let remoteTouched = false
        for (const pgno of allPages) {
            const p = pgno as Pgno
            const baseHash = await pageHashForView('base', p)
            if (await pageHashForView('local', p) !== baseHash) localTouched = true
            if (await pageHashForView('remote', p) !== baseHash) remoteTouched = true
            if (localTouched && remoteTouched) break
        }
        if (!localTouched || !remoteTouched) continue

        const zeroPage = new Uint8Array(pageSize)
        const zeroHash = manifest.hashPage?.(zeroPage)
        if (zeroHash == null) throw new Error('MISSING_PAGE_HASHER')

        const dataEntry = entriesByName.get(`${group.name}_data`)
        const idxEntry = entriesByName.get(`${group.name}_idx`)
        const contentEntry = entriesByName.get(`${group.name}_content`)
        const docsizeEntry = entriesByName.get(`${group.name}_docsize`)
        const configEntry = entriesByName.get(`${group.name}_config`)
        let ftsReconstructed = false

        // Legalish owns this FTS5 table as a derived projection from
        // legalish_search_index. Keep the SQLite-private shadow tables opaque:
        // mixed segment reconstruction can pass B-tree parsing while producing
        // a malformed FTS5 inverted index.
        const keepOpaqueDerivedProjection = group.name === 'legalish_search_fts'
        if (!keepOpaqueDerivedProjection
            && dataEntry?.rootPage
            && idxEntry?.rootPage
            && contentEntry?.rootPage
            && docsizeEntry?.rootPage) {
            try {
            const tableCells = async (
                entry: SqliteMasterEntry,
                view: 'base' | 'local' | 'remote',
            ): Promise<Map<bigint, BTreeCell>> => {
                const viewEntry = entryByNameForView(view, entry.name) ?? entry
                const cells = await collectTableCellsFromBtree(
                    viewEntry.rootPage,
                    pageSize,
                    p => pageBytesForView(view, p),
                )
                return tableCellsByRowId(cells ?? [])
            }
            const indexCells = async (
                entry: SqliteMasterEntry,
                view: 'base' | 'local' | 'remote',
            ): Promise<IndexCell[]> => {
                const viewEntry = entryByNameForView(view, entry.name) ?? entry
                const cells: IndexCell[] = []
                for (const pgno of await collectBtreePagesWithOverflow(
                    viewEntry.rootPage,
                    p => pageBytesForView(view, p),
                    pageSize,
                )) {
                    const page = await pageBytesForView(view, pgno as Pgno)
                    if (page == null) continue
                    const pageType = getPageType(page, pgno)
                    if (pageType === BTreePageType.IndexLeaf) {
                        cells.push(...await extractIndexCells(page, pgno, p => pageBytesForView(view, p as Pgno), pageSize))
                    } else if (pageType === BTreePageType.IndexInterior) {
                        cells.push(...await extractInteriorIndexCells(page, pgno, p => pageBytesForView(view, p as Pgno), pageSize))
                    }
                }
                return cells
            }

            const baseContentCells = await tableCells(contentEntry, 'base')
            const localContentCells = await tableCells(contentEntry, 'local')
            const remoteContentCells = await tableCells(contentEntry, 'remote')
            const mergedContentCells = new Map<bigint, BTreeCell>(remoteContentCells)
            const localDocIdRemaps = new Map<bigint, bigint>()
            let nextDocId = Math.max(
                0,
                ...[...remoteContentCells.keys(), ...localContentCells.keys()].map(Number),
            ) + 1
            for (const [rowId, localCell] of localContentCells) {
                const baseCell = baseContentCells.get(rowId)
                const remoteCell = remoteContentCells.get(rowId)
                const localChangedCell = baseCell == null || !uint8ArrayEqual(localCell.payloadHash, baseCell.payloadHash)
                if (!localChangedCell) continue
                if (remoteCell == null || uint8ArrayEqual(remoteCell.payloadHash, localCell.payloadHash)) {
                    mergedContentCells.set(rowId, localCell)
                    continue
                }
                while (mergedContentCells.has(BigInt(nextDocId))) nextDocId += 1
                const newRowId = BigInt(nextDocId)
                nextDocId += 1
                localDocIdRemaps.set(rowId, newRowId)
                mergedContentCells.set(newRowId, makeTableCell(newRowId, localCell.payload))
            }

            const baseDataCells = await tableCells(dataEntry, 'base')
            const localDataCells = await tableCells(dataEntry, 'local')
            const remoteDataCells = await tableCells(dataEntry, 'remote')
            const baseStruct = parseFts5StructureRecord(extractFts5DataBlock(baseDataCells.get(10n)))
            const localStruct = parseFts5StructureRecord(extractFts5DataBlock(localDataCells.get(10n)))
            const remoteStruct = parseFts5StructureRecord(extractFts5DataBlock(remoteDataCells.get(10n)))
            const docIdDelta = deriveConstantRowIdDelta(localDocIdRemaps)

            if (localStruct != null && remoteStruct != null && docIdDelta != null) {
                const baseSegIds = new Set((baseStruct?.segments ?? []).map(segment => segment.id))
                const remoteSegIds = new Set(remoteStruct.segments.map(segment => segment.id))
                let nextSegId = Math.max(0, ...remoteSegIds, ...baseSegIds) + 1
                const localSegIdMap = new Map<number, number>()
                for (const segment of localStruct.segments) {
                    if (baseSegIds.has(segment.id)) continue
                    while (remoteSegIds.has(nextSegId) || baseSegIds.has(nextSegId)) nextSegId += 1
                    localSegIdMap.set(segment.id, nextSegId)
                    remoteSegIds.add(nextSegId)
                    nextSegId += 1
                }

                const groupPages = new Map<number, Uint8Array>()
                const groupWrittenPages = new Set<number>()
                const ftsRootPages = new Set<number>([
                    dataEntry.rootPage,
                    idxEntry.rootPage,
                    contentEntry.rootPage,
                    docsizeEntry.rootPage,
                    ...(configEntry?.rootPage ? [configEntry.rootPage] : []),
                ])
                const pagesByEntryName = new Map<string, Set<number>>()
                const collectPagesForEntry = async (entry: SqliteMasterEntry): Promise<Set<number>> => {
                    const pages = new Set<number>()
                    for (const view of ['base', 'local', 'remote'] as const) {
                        const viewEntry = entryByNameForView(view, entry.name) ?? entry
                        if (!viewEntry.rootPage) continue
                        for (const pgno of await collectBtreePagesWithOverflow(
                            viewEntry.rootPage,
                            p => pageBytesForView(view, p),
                            pageSize,
                        )) {
                            pages.add(pgno)
                        }
                    }
                    return pages
                }
                for (const entry of [contentEntry, docsizeEntry, dataEntry, idxEntry, configEntry].filter((entry): entry is SqliteMasterEntry => entry != null)) {
                    pagesByEntryName.set(entry.name, await collectPagesForEntry(entry))
                }
                const reusablePagesForEntry = (entry: SqliteMasterEntry): number[] =>
                    [...(pagesByEntryName.get(entry.name) ?? [])]
                        .filter(pgno =>
                            pgno > 1
                            && pgno !== entry.rootPage
                            && !ftsRootPages.has(pgno)
                            && !groupWrittenPages.has(pgno))
                        .sort((left, right) => left - right)
                const appendPages = (rebuilt: { pages: Map<number, Uint8Array>; currentPageCount: number }): void => {
                    for (const [pgno, page] of rebuilt.pages) {
                        const existing = groupPages.get(pgno)
                        if (existing != null && !uint8ArrayEqual(existing, page)) {
                            throw new Error(`SQLITE3MT_FTS5_PAGE_OWNER_COLLISION: page ${pgno} rebuilt by multiple FTS5 shadow owners`)
                        }
                        groupPages.set(pgno, page)
                        groupWrittenPages.add(pgno)
                    }
                    resultPageCount = Math.max(resultPageCount, rebuilt.currentPageCount)
                }

                appendPages(buildTableTreePages(
                    contentEntry.rootPage,
                    [...mergedContentCells.values()],
                    pageSize,
                    resultPageCount,
                    undefined,
                    reusablePagesForEntry(contentEntry),
                ))

                const remoteDocsizeCells = await tableCells(docsizeEntry, 'remote')
                const localDocsizeCells = await tableCells(docsizeEntry, 'local')
                const mergedDocsizeCells = new Map<bigint, BTreeCell>(remoteDocsizeCells)
                for (const [rowId, localCell] of localDocsizeCells) {
                    const targetRowId = localDocIdRemaps.get(rowId) ?? rowId
                    if (remoteDocsizeCells.has(targetRowId) && targetRowId === rowId) continue
                    mergedDocsizeCells.set(targetRowId, makeTableCell(targetRowId, localCell.payload))
                }
                appendPages(buildTableTreePages(
                    docsizeEntry.rootPage,
                    [...mergedDocsizeCells.values()],
                    pageSize,
                    resultPageCount,
                    undefined,
                    reusablePagesForEntry(docsizeEntry),
                ))

                const mergedDataCells = new Map<bigint, BTreeCell>()
                for (const [rowId, cell] of remoteDataCells) {
                    if (rowId !== 1n && rowId !== 10n) mergedDataCells.set(rowId, cell)
                }
                for (const [rowId, cell] of localDataCells) {
                    if (rowId === 1n || rowId === 10n) continue
                    const decoded = decodeFts5DataRowId(rowId)
                    if (decoded == null) continue
                    const newSegId = localSegIdMap.get(decoded.segId)
                    if (newSegId == null) continue
                    const newRowId = encodeFts5DataRowId({ ...decoded, segId: newSegId })
                    const block = extractFts5DataBlock(cell)
                    const rewrittenBlock = decoded.dlidx
                        ? remapFts5DlidxRowids(block, localDocIdRemaps)
                        : remapFts5LeafRowids(block, localDocIdRemaps)
                    mergedDataCells.set(newRowId, makeTableCell(newRowId, buildFts5DataPayload(rewrittenBlock)))
                }
                const mergedStruct = mergeFts5Structures(remoteStruct, localStruct, localSegIdMap)
                mergedDataCells.set(10n, makeTableCell(10n, buildFts5DataPayload(encodeFts5StructureRecord(mergedStruct))))
                mergedDataCells.set(1n, makeTableCell(
                    1n,
                    buildFts5DataPayload(mergeFts5Averages(
                        extractFts5DataBlock(baseDataCells.get(1n)),
                        extractFts5DataBlock(localDataCells.get(1n)),
                        extractFts5DataBlock(remoteDataCells.get(1n)),
                    )),
                ))
                appendPages(buildTableTreePages(
                    dataEntry.rootPage,
                    [...mergedDataCells.values()],
                    pageSize,
                    resultPageCount,
                    undefined,
                    reusablePagesForEntry(dataEntry),
                ))

                const remoteIdxCells = await indexCells(idxEntry, 'remote')
                const localIdxCells = await indexCells(idxEntry, 'local')
                const mergedIdxCells = new Map<string, IndexCell>()
                for (const cell of remoteIdxCells) mergedIdxCells.set(indexCellKey(cell), cell)
                for (const cell of localIdxCells) {
                    const remapped = remapFts5IdxSegment(cell, localSegIdMap)
                    if (remapped != null) mergedIdxCells.set(indexCellKey(remapped), remapped)
                }
                appendPages(buildIndexTreePages(
                    idxEntry.rootPage,
                    [...mergedIdxCells.values()],
                    pageSize,
                    resultPageCount,
                    reusablePagesForEntry(idxEntry),
                    compareSqliteRecords,
                ))

                if (configEntry?.rootPage) {
                    const baseConfigCells = await indexCells(configEntry, 'base')
                    const localConfigCells = await indexCells(configEntry, 'local')
                    const remoteConfigCells = await indexCells(configEntry, 'remote')
                    const mergedConfigCells = mergeIndexCellSetsDedupingUniqueKeys(
                        baseConfigCells,
                        localConfigCells,
                        remoteConfigCells,
                        true,
                    )
                    appendPages(buildIndexTreePages(
                        configEntry.rootPage,
                        mergedConfigCells,
                        pageSize,
                        resultPageCount,
                        reusablePagesForEntry(configEntry),
                        compareSqliteRecords,
                    ))
                }

                const writtenPages = new Set(groupPages.keys())
                for (const pgno of allPages) {
                    const p = pgno as Pgno
                    handledOpaquePages.add(p)
                    if (writtenPages.has(pgno)) continue
                    resultHashByPgno.set(p, zeroHash)
                    importBlobsByPgno.set(p, zeroPage)
                    importBlobsByHash.set(zeroHash, zeroPage)
                    freePages.add(pgno)
                }
                for (const [pgno, page] of groupPages) {
                    const p = pgno as Pgno
                    handledOpaquePages.add(p)
                    freePages.delete(pgno)
                    const hash = manifest.hashPage?.(page)
                    if (hash == null) throw new Error('MISSING_PAGE_HASHER')
                    resultHashByPgno.set(p, hash)
                    if (hash !== localNorm.get(p)) {
                        importBlobsByPgno.set(p, page)
                        importBlobsByHash.set(hash, page)
                    }
                }
                ftsReconstructed = true
            }
            } catch {
                // Unsupported FTS5 shadow shape: fall back to one consistent
                // branch rather than publishing a partial derived-index mix.
                ftsReconstructed = false
            }
        }

        if (!ftsReconstructed) {
            const digestForView = async (view: 'local' | 'remote'): Promise<string> => {
                const parts: string[] = []
                for (const pgno of [...(pagesByView.get(view) ?? [])].sort((a, b) => a - b)) {
                    parts.push(`${pgno}:${await pageHashForView(view, pgno as Pgno) ?? 'null'}`)
                }
                return parts.join('|')
            }
            const localDigest = await digestForView('local')
            const remoteDigest = await digestForView('remote')
            const winner: 'local' | 'remote' = localDigest >= remoteDigest ? 'local' : 'remote'

            for (const pgno of allPages) {
                const p = pgno as Pgno
                handledOpaquePages.add(p)
                const winnerHash = await pageHashForView(winner, p)
                if (winnerHash != null && (pagesByView.get(winner)?.has(pgno) ?? false)) {
                    resultHashByPgno.set(p, winnerHash)
                    const currentLocalHash = await pageHashForView('local', p)
                    if (winnerHash !== currentLocalHash) {
                        const bytes = await pageBytesForView(winner, p)
                        if (bytes == null) throw new Error('MISSING_FTS5_OPAQUE_IMPORT_BLOB')
                        importBlobsByPgno.set(p, bytes)
                        importBlobsByHash.set(winnerHash, bytes)
                    }
                    continue
                }

                resultHashByPgno.set(p, zeroHash)
                importBlobsByPgno.set(p, zeroPage)
                importBlobsByHash.set(zeroHash, zeroPage)
                freePages.add(pgno)
            }
        }
    }

    const btreeOwnershipByView = new Map<'base' | 'local' | 'remote', Map<number, Set<BtreeOwner>>>()
    for (const view of ['base', 'local', 'remote'] as const) {
        btreeOwnershipByView.set(view, await collectBtreeOwnership(
            entriesForView(view),
            pageSize,
            p => pageBytesForView(view, p),
        ))
    }

    for (const entry of masterEntries) {
        if (
            entry.type !== 'table'
            || entry.rootPage <= 0
            || fts5ShadowTableNames.has(entry.name)
            || !tableHasMergeableWithoutRowidKey(entry)
        ) continue

        const pagesByView = new Map<'base' | 'local' | 'remote', Set<number>>()
        for (const view of ['base', 'local', 'remote'] as const) {
            const viewEntry = entryForView(entry, view)
            pagesByView.set(view, viewEntry == null || viewEntry.rootPage <= 0
                ? new Set()
                : await collectBtreePagesWithOverflow(
                    viewEntry.rootPage,
                    p => pageBytesForView(view, p),
                    pageSize,
                ))
        }

        const allPages = new Set<number>([
            ...(pagesByView.get('base') ?? []),
            ...(pagesByView.get('local') ?? []),
            ...(pagesByView.get('remote') ?? []),
        ])
        if (allPages.size === 0) continue

        let localTouched = false
        let remoteTouched = false
        for (const pgno of allPages) {
            const p = pgno as Pgno
            const baseHash = await pageHashForView('base', p)
            if (await pageHashForView('local', p) !== baseHash) localTouched = true
            if (await pageHashForView('remote', p) !== baseHash) remoteTouched = true
            if (localTouched && remoteTouched) break
        }
        if (!localTouched || !remoteTouched) continue

        const collectViewCells = async (view: SqliteMergeView): Promise<IndexCell[] | null> => {
            const viewEntry = entryForView(entry, view)
            if (viewEntry == null || viewEntry.rootPage <= 0) return []
            return collectIndexCellsFromBtree(
                viewEntry.rootPage,
                pageSize,
                p => pageBytesForView(view, p),
            )
        }
        const [baseCells, localCells, remoteCells] = await Promise.all([
            collectViewCells('base'),
            collectViewCells('local'),
            collectViewCells('remote'),
        ])
        if (baseCells == null || localCells == null || remoteCells == null) continue

        const primaryKeyColumnCount = withoutRowidPrimaryKeyColumnCount(entry)
        const mergedCells = mergeIndexCellsRebasingRowIdCollisions(
            baseCells,
            localCells,
            remoteCells,
            false,
            true,
            primaryKeyColumnCount,
        )

        const owner = sqliteMasterEntryOwner(entry)
        const previousPages = new Set([...allPages]
            .filter(pgno => !pageHasForeignOwner(btreeOwnershipByView, pgno, owner)))
        const rebuilt = buildIndexTreePages(
            entry.rootPage,
            mergedCells,
            pageSize,
            resultPageCount,
        )
        resultPageCount = Math.max(resultPageCount, rebuilt.currentPageCount)

        const zeroPage = new Uint8Array(pageSize)
        const zeroHash = manifest.hashPage?.(zeroPage)
        if (zeroHash == null) throw new Error('MISSING_PAGE_HASHER')
        const writtenPages = new Set(rebuilt.pages.keys())
        for (const pgno of previousPages) {
            const p = pgno as Pgno
            handledOpaquePages.add(p)
            if (writtenPages.has(pgno)) continue
            resultHashByPgno.set(p, zeroHash)
            importBlobsByPgno.set(p, zeroPage)
            importBlobsByHash.set(zeroHash, zeroPage)
            freePages.add(pgno)
        }

        for (const [pgno, page] of rebuilt.pages) {
            const p = pgno as Pgno
            handledOpaquePages.add(p)
            freePages.delete(pgno)
            const hash = manifest.hashPage?.(page)
            if (hash == null) throw new Error('MISSING_PAGE_HASHER')
            resultHashByPgno.set(p, hash)
            if (hash !== await pageHashForView('local', p)) {
                importBlobsByPgno.set(p, page)
                importBlobsByHash.set(hash, page)
            }
        }

        semanticallyMergedLogicalTables.add(normalizeSqlIdentifier(entry.name))
    }

    for (const entry of masterEntries) {
        if (
            entry.type !== 'table'
            || entry.rootPage <= 0
            || fts5ShadowTableNames.has(entry.name)
            || !tableHasLogicalRowidTableKey(entry)
        ) continue

        const pagesByView = new Map<'base' | 'local' | 'remote', Set<number>>()
        for (const view of ['base', 'local', 'remote'] as const) {
            const viewEntry = entryForView(entry, view)
            pagesByView.set(view, viewEntry == null || viewEntry.rootPage <= 0
                ? new Set()
                : await collectBtreePagesWithOverflow(
                    viewEntry.rootPage,
                    p => pageBytesForView(view, p),
                    pageSize,
                ))
        }

        const allPages = new Set<number>([
            ...(pagesByView.get('base') ?? []),
            ...(pagesByView.get('local') ?? []),
            ...(pagesByView.get('remote') ?? []),
        ])
        if (allPages.size === 0) continue

        let localTouched = false
        let remoteTouched = false
        for (const pgno of allPages) {
            const p = pgno as Pgno
            const baseHash = await pageHashForView('base', p)
            if (await pageHashForView('local', p) !== baseHash) localTouched = true
            if (await pageHashForView('remote', p) !== baseHash) remoteTouched = true
            if (localTouched && remoteTouched) break
        }
        if (!localTouched || !remoteTouched) continue

        const logicalKeyInfo = buildLogicalTableKeyInfo(entry)
        const collectViewTable = (
            view: SqliteMergeView,
        ): Promise<CollectedTableBtree | null> => {
            const viewEntry = entryForView(entry, view)
            if (viewEntry == null || viewEntry.rootPage <= 0) {
                return Promise.resolve({ cells: [], pages: new Set(), cellsByPage: new Map() })
            }
            return collectValidatedLogicalTableBtree(
                viewEntry,
                entriesForView(view),
                pageSize,
                p => pageBytesForView(view, p),
            )
        }
        const baseTable = await collectViewTable('base')
        const localTable = await collectViewTable('local')
        const remoteTable = await collectViewTable('remote')
        if (baseTable == null || localTable == null || remoteTable == null) continue

        const merged = mergeLeafCells(
            baseTable.cells,
            localTable.cells,
            remoteTable.cells,
            true,
            logicalKeyInfo,
        )
        if (!merged.success) continue

        const owner = sqliteMasterEntryOwner(entry)
        const previousPages = new Set([...allPages]
            .filter(pgno => !pageHasForeignOwner(btreeOwnershipByView, pgno, owner)))
        const rootTemplate = entry.rootPage === 1
            ? await pageBytesForView('local', 1 as Pgno) ?? undefined
            : undefined
        const rebuilt = buildTableTreePages(
            entry.rootPage,
            merged.mergedCells,
            pageSize,
            resultPageCount,
            rootTemplate,
        )
        resultPageCount = Math.max(resultPageCount, rebuilt.currentPageCount)

        const zeroPage = new Uint8Array(pageSize)
        const zeroHash = manifest.hashPage?.(zeroPage)
        if (zeroHash == null) throw new Error('MISSING_PAGE_HASHER')
        const writtenPages = new Set(rebuilt.pages.keys())
        for (const pgno of previousPages) {
            const p = pgno as Pgno
            handledOpaquePages.add(p)
            if (writtenPages.has(pgno)) continue
            resultHashByPgno.set(p, zeroHash)
            importBlobsByPgno.set(p, zeroPage)
            importBlobsByHash.set(zeroHash, zeroPage)
            freePages.add(pgno)
        }

        for (const [pgno, page] of rebuilt.pages) {
            const p = pgno as Pgno
            handledOpaquePages.add(p)
            freePages.delete(pgno)
            const hash = manifest.hashPage?.(page)
            if (hash == null) throw new Error('MISSING_PAGE_HASHER')
            resultHashByPgno.set(p, hash)
            if (hash !== await pageHashForView('local', p)) {
                importBlobsByPgno.set(p, page)
                importBlobsByHash.set(hash, page)
            }
        }

        semanticallyMergedLogicalTables.add(normalizeSqlIdentifier(entry.name))
    }

    const pageOwners = (
        view: SqliteMergeView,
        pgno: Pgno,
    ): ReadonlySet<BtreeOwner> => btreeOwnershipByView.get(view)?.get(pgno) ?? new Set()

    const ownersConflict = (
        left: ReadonlySet<BtreeOwner>,
        right: ReadonlySet<BtreeOwner>,
    ): boolean => {
        if (left.size === 0 || right.size === 0 || left.size !== right.size) {
            return left.size > 0 && right.size > 0
        }
        for (const owner of left) {
            if (!right.has(owner)) return true
        }
        return false
    }

    for (const pgno of remoteChanged) {
        if (handledOpaquePages.has(pgno)) continue
        // Resolve base and remote hashes
        setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:base-hash`)
        const baseHash = await manifest.getPageHashFromConcreteRoot(baseRoot, pgno)
        baseHashes.set(pgno, baseHash)

        setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:remote-hash`)
        const remoteHash = await manifest.getPageHashFromConcreteRoot(remoteRoot, pgno)
        remoteHashes.set(pgno, remoteHash)

        // Local hash: from normalized local view, or fall back to base
        const localHash: PageHash | null = localNorm.get(pgno) ?? baseHash

        // Skip import for pages above resultPageCount
        if (pgno > resultPageCount) continue

        // Three-way merge for this page
        if (localHash === baseHash) {
            // Local didn't change → remote wins
            if (remoteHash != null && remoteHash !== baseHash) {
                setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:remote-win-fetch`)
                const bytes = await manifest.getPageBytesByHash(remoteHash)
                if (bytes == null) {
                    throw new Error('MISSING_REMOTE_IMPORT_BLOB')
                }
                importBlobsByPgno.set(pgno, bytes)
                importBlobsByHash.set(remoteHash, bytes)
                remotePagesCache.set(pgno, bytes)
                resultHashByPgno.set(pgno, remoteHash)
            }
        } else if (localHash === remoteHash) {
            // Both made the same change → no import required; local SQLite
            // already has the final bytes and reconcile only needs the hash.
            if (remoteHash != null) {
                resultHashByPgno.set(pgno, remoteHash)
            }
        } else if (remoteHash === baseHash) {
            // Only local changed → no import needed, local stays in residual
            if (localHash != null && localHash !== baseHash) {
                resultHashByPgno.set(pgno, localHash)
            }
        } else {
            const localOwners = pageOwners('local', pgno)
            const remoteOwners = pageOwners('remote', pgno)
            if (localHash != null
                && remoteHash != null
                && ownersConflict(localOwners, remoteOwners)) {
                for (const owner of localOwners) {
                    const tableName = ownerTableNames.get(owner)
                    if (tableName != null) semanticallyMergedLogicalTables.add(tableName)
                }
                resultHashByPgno.set(pgno, remoteHash)
                continue
            }

            setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-start`)
            const semanticMerge = await trySemanticLeafPageMerge(
                manifest,
                baseRoot,
                remoteRoot,
                localNorm,
                baseHashes,
                remoteHashes,
                pgno,
                baseHash,
                localHash,
                remoteHash,
                resultPageCount,
            )
            if (semanticMerge != null) {
                setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-complete`)
                resultPageCount = Math.max(resultPageCount, semanticMerge.resultPageCount)
                for (const [mergedPgno, mergedHash] of semanticMerge.pageHashes) {
                    resultHashByPgno.set(mergedPgno, mergedHash)
                    const currentLocalHash = localNorm.get(mergedPgno) ?? (
                        mergedPgno === pgno ? baseHash : null
                    )
                    if (mergedHash !== currentLocalHash) {
                        const bytes = semanticMerge.pageBytes.get(mergedPgno)
                        if (bytes == null) {
                            throw new Error('MISSING_SEMANTIC_IMPORT_BLOB')
                        }
                        importBlobsByPgno.set(mergedPgno, bytes)
                        importBlobsByHash.set(mergedHash, bytes)
                    }
                }
                for (const tableName of semanticMerge.affectedTableNames) {
                    semanticallyMergedLogicalTables.add(tableName)
                }
                for (const handledPgno of semanticMerge.handledPages) {
                    handledOpaquePages.add(handledPgno)
                }
                if (!semanticMerge.handledPages.has(pgno) && remoteHash != null) {
                    resultHashByPgno.set(pgno, remoteHash)
                    if (remoteHash !== localHash) {
                        const bytes = await manifest.getPageBytesByHash(remoteHash)
                        if (bytes == null) {
                            throw new Error('MISSING_UNHANDLED_COLLISION_REMOTE_BLOB')
                        }
                        importBlobsByPgno.set(pgno, bytes)
                        importBlobsByHash.set(remoteHash, bytes)
                    }
                }
                continue
            }

            // Irreducible conflict: choose a client-independent canonical
            // content hash so all peers converge even when the semantic layer
            // cannot preserve both facts in the current SQLite page shape.
            setMergePlanDebugStage(manifest, `build-merge-plan:pg${pgno}:semantic-conflict-canonical-win`)
            const chosenHash = canonicalHash(localHash, remoteHash)
            if (chosenHash != null && chosenHash !== baseHash) {
                resultHashByPgno.set(pgno, chosenHash)
                if (chosenHash !== localHash) {
                    const bytes = await manifest.getPageBytesByHash(chosenHash)
                    if (bytes == null) {
                        throw new Error('MISSING_CANONICAL_IMPORT_BLOB')
                    }
                    importBlobsByPgno.set(pgno, bytes)
                    importBlobsByHash.set(chosenHash, bytes)
                }
            }
        }
    }

    const finalPageHash = async (pgno: Pgno): Promise<PageHash | null> => {
        if (pgno > resultPageCount) return null
        if (resultHashByPgno.has(pgno)) return resultHashByPgno.get(pgno) ?? null
        if (!remoteChanged.has(pgno)) {
            if (localNorm.has(pgno)) return localNorm.get(pgno) ?? null
            return pageHashForView('base', pgno)
        }
        return pageHashForView('remote', pgno)
    }
    const finalPageBytes = async (pgno: Pgno): Promise<Uint8Array | null> => {
        const hash = await finalPageHash(pgno)
        if (hash == null) return null
        return importBlobsByHash.get(hash) ?? manifest.getPageBytesByHash(hash)
    }
    const collectFinalReachablePages = async (): Promise<Set<number>> => {
        const finalEntries = await collectSqliteMasterEntries(pageSize, finalPageBytes)
        const reachablePages = new Set<number>()
        for (const pgno of await collectBtreePagesWithOverflow(1, finalPageBytes, pageSize)) {
            reachablePages.add(pgno)
        }
        for (const entry of finalEntries) {
            if (entry.rootPage <= 0) continue
            for (const pgno of await collectBtreePagesWithOverflow(entry.rootPage, finalPageBytes, pageSize)) {
                reachablePages.add(pgno)
            }
        }
        return reachablePages
    }

    if (semanticallyMergedLogicalTables.size > 0) {
        setMergePlanDebugStage(manifest, 'build-merge-plan:rebuild-logical-table-indexes')
        const hashPage = manifest.hashPage
        if (hashPage == null) throw new Error('MISSING_PAGE_HASHER')
        const finalEntries = await collectSqliteMasterEntries(pageSize, finalPageBytes)
        const finalTablesByName = new Map(finalEntries
            .filter(entry => entry.type === 'table')
            .map(entry => [normalizeSqlIdentifier(entry.name), entry]))

        const writeRebuiltPage = (pgno: Pgno, page: Uint8Array): void => {
            const hash = hashPage(page)
            resultHashByPgno.set(pgno, hash)
            if (hash !== localNorm.get(pgno)) {
                importBlobsByPgno.set(pgno, page)
                importBlobsByHash.set(hash, page)
            }
        }

        for (const tableName of semanticallyMergedLogicalTables) {
            const table = finalTablesByName.get(tableName)
            if (table == null || table.rootPage <= 0) continue
            const rowidTableBtree = tableHasLogicalRowidTableKey(table)
                ? await collectTableBtree(table.rootPage, pageSize, finalPageBytes)
                : null
            const withoutRowidTableCells = tableHasMergeableWithoutRowidKey(table)
                ? await collectIndexCellsFromBtree(table.rootPage, pageSize, finalPageBytes)
                : null
            if (rowidTableBtree == null && withoutRowidTableCells == null) continue

            for (const index of finalEntries) {
                if (index.type !== 'index' || index.rootPage <= 0) continue
                if (normalizeSqlIdentifier(index.tblName) !== tableName) continue

                const indexCells = rowidTableBtree != null
                    ? buildIndexCellsFromTable(
                        index,
                        table,
                        rowidTableBtree.cells,
                        indexRecordComparator(index, table) ?? compareSqliteRecords,
                    )
                    : buildIndexCellsFromWithoutRowidTable(
                        index,
                        table,
                        withoutRowidTableCells!,
                        indexRecordComparator(index, table) ?? compareSqliteRecords,
                    )
                if (indexCells == null) continue

                const previousPages = await collectBtreePagesWithOverflow(index.rootPage, finalPageBytes, pageSize)
                const writtenPages = new Set<number>()
                const rebuilt = buildIndexTreePages(
                    index.rootPage,
                    indexCells,
                    pageSize,
                    resultPageCount,
                    [],
                    indexRecordComparator(index, table) ?? compareSqliteRecords,
                )
                resultPageCount = Math.max(resultPageCount, rebuilt.currentPageCount)
                for (const [pageNo, page] of rebuilt.pages) {
                    writtenPages.add(pageNo)
                    writeRebuiltPage(pageNo as Pgno, page)
                }

                for (const pgno of previousPages) {
                    if (pgno <= 1 || writtenPages.has(pgno)) continue
                    freePages.add(pgno)
                }
                for (const pgno of writtenPages) {
                    freePages.delete(pgno)
                }
            }
        }
    }

    const shouldReconcilePageOwnership = page1ForMaster != null
        && masterEntries.length > 0
        && (
            handledOpaquePages.size > 0
            || semanticallyMergedLogicalTables.size > 0
            || resultPageCount > Math.min(basePageCount, localPageCount, remotePageCount)
        )
    if (shouldReconcilePageOwnership) {
        const reachablePages = await collectFinalReachablePages()

        const candidatePages = new Set<number>()
        for (const pgno of localNorm.keys()) candidatePages.add(pgno)
        for (const pgno of remoteChanged) candidatePages.add(pgno)
        for (const pgno of resultHashByPgno.keys()) candidatePages.add(pgno)
        const firstGrowthPage = Math.min(basePageCount, localPageCount, remotePageCount) + 1
        for (let pgno = firstGrowthPage; pgno <= resultPageCount; pgno += 1) {
            candidatePages.add(pgno)
        }
        for (const view of SQLITE_MERGE_VIEWS) {
            const pageCount = view === 'base'
                ? basePageCount
                : view === 'local'
                    ? localPageCount
                    : remotePageCount
            const page1 = await pageBytesForView(view, 1 as Pgno)
            for (const pgno of await collectFreelistPageNumbers(
                page1,
                pgno => pageBytesForView(view, pgno),
                pageSize,
                pageCount,
            )) {
                candidatePages.add(pgno)
            }
        }

        const zeroPage = new Uint8Array(pageSize)
        const zeroHash = manifest.hashPage?.(zeroPage)
        if (zeroHash == null) throw new Error('MISSING_PAGE_HASHER')
        for (const pgno of candidatePages) {
            if (pgno <= 1 || pgno > resultPageCount || reachablePages.has(pgno)) continue
            const hash = await finalPageHash(pgno as Pgno)
            if (hash == null) {
                freePages.add(pgno)
                resultHashByPgno.set(pgno as Pgno, zeroHash)
                importBlobsByPgno.set(pgno as Pgno, zeroPage)
                importBlobsByHash.set(zeroHash, zeroPage)
                continue
            }
            freePages.add(pgno)
            resultHashByPgno.set(pgno as Pgno, zeroHash)
            if (zeroHash !== localNorm.get(pgno as Pgno)) {
                importBlobsByPgno.set(pgno as Pgno, zeroPage)
                importBlobsByHash.set(zeroHash, zeroPage)
            }
        }
    }

    const page1Pgno = 1 as Pgno
    const remotePage1Hash = remoteHashes.get(page1Pgno)
        ?? await manifest.getPageHashFromConcreteRoot(remoteRoot, page1Pgno)
    const basePage1Hash = baseHashes.get(page1Pgno)
        ?? await manifest.getPageHashFromConcreteRoot(baseRoot, page1Pgno)
    remoteHashes.set(page1Pgno, remotePage1Hash)
    baseHashes.set(page1Pgno, basePage1Hash)
    const sourcePage1Hash = resultHashByPgno.get(page1Pgno)
        ?? localNorm.get(page1Pgno)
        ?? remotePage1Hash
        ?? basePage1Hash
    if (sourcePage1Hash != null) {
        const sourcePage1Bytes = importBlobsByHash.get(sourcePage1Hash)
            ?? await manifest.getPageBytesByHash(sourcePage1Hash)
        if (sourcePage1Bytes == null) {
            throw new Error('MISSING_PAGE1_HEADER_BLOB')
        }
        if (sourcePage1Bytes.byteLength < 32) {
            setMergePlanDebugStage(manifest, 'build-merge-plan:page1-short-skip-header-fix')
        } else {
            const sourcePageCount = new DataView(
                sourcePage1Bytes.buffer,
                sourcePage1Bytes.byteOffset,
                sourcePage1Bytes.byteLength,
            ).getUint32(28)
            if (sourcePageCount !== resultPageCount) {
                const page1Bytes = sourcePage1Bytes.slice()
                new DataView(page1Bytes.buffer, page1Bytes.byteOffset, page1Bytes.byteLength)
                    .setUint32(28, resultPageCount)
                const hashPage = manifest.hashPage
                if (!hashPage) throw new Error('MISSING_PAGE_HASHER')
                const page1Hash = hashPage(page1Bytes)
                resultHashByPgno.set(page1Pgno, page1Hash)
                if (page1Hash !== localNorm.get(page1Pgno)) {
                    importBlobsByPgno.set(page1Pgno, page1Bytes)
                    importBlobsByHash.set(page1Hash, page1Bytes)
                }
            }
        }
    }

    if (freePages.size > 0) {
        if (page1ForMaster != null && masterEntries.length > 0) {
            removeReachablePagesFromFreeSet(freePages, await collectFinalReachablePages(), resultPageCount)
        }
        const freelist = buildFreelistPages([...freePages], pageSize)
        if (freelist != null) {
            const page1Hash = resultHashByPgno.get(page1Pgno)
                ?? localNorm.get(page1Pgno)
                ?? remotePage1Hash
                ?? basePage1Hash
            if (page1Hash == null) {
                throw new Error('MISSING_FREELIST_PAGE1_HASH')
            }
            const page1Bytes = importBlobsByHash.get(page1Hash)
                ?? await manifest.getPageBytesByHash(page1Hash)
            if (page1Bytes == null) {
                throw new Error('MISSING_FREELIST_PAGE1_BLOB')
            }
            const nextPage1 = page1Bytes.slice()
            const view = new DataView(nextPage1.buffer, nextPage1.byteOffset, nextPage1.byteLength)
            view.setUint32(28, resultPageCount)
            view.setUint32(32, freelist.firstTrunkPgno)
            view.setUint32(36, freelist.totalFreePages)
            const hashPage = manifest.hashPage
            if (!hashPage) throw new Error('MISSING_PAGE_HASHER')

            const nextPage1Hash = hashPage(nextPage1)
            resultHashByPgno.set(page1Pgno, nextPage1Hash)
            if (nextPage1Hash !== localNorm.get(page1Pgno)) {
                importBlobsByPgno.set(page1Pgno, nextPage1)
                importBlobsByHash.set(nextPage1Hash, nextPage1)
            }

            for (const [pgno, page] of freelist.pages) {
                const hash = hashPage(page)
                const p = pgno as Pgno
                resultHashByPgno.set(p, hash)
                if (hash !== localNorm.get(p)) {
                    importBlobsByPgno.set(p, page)
                    importBlobsByHash.set(hash, page)
                }
            }
        }
    }

    setMergePlanDebugStage(manifest, 'build-merge-plan:done')
    return {
        baseRoot,
        baseVersion,
        basePageCount,
        remoteRoot,
        remoteVersion,
        remotePageCount,
        walSalt0: wal.salt0,
        walSalt1: wal.salt1,
        caughtUpFrameMax: pendingDbDeltasIntegratedFrameMax,
        liveLocalNorm: localNorm,
        liveLocalPageCount: localPageCount,
        remoteChanged,
        remoteHashes,
        baseHashes,
        resultHashByPgno,
        resultPageCount,
        importBlobsByPgno,
        importBlobsByHash,
        basePagesCache,
        remotePagesCache,
    }
}

// ── §15.5.1 getFinalResultPageHash + metadata carrier frame ────

export interface MetadataCarrierRuntime {
    stagedCas: Map<PageHash, Uint8Array>
    getBlob(hash: PageHash): Promise<Uint8Array | null>
    rpcGetBlob(hash: PageHash): Promise<Uint8Array | null>
}

/**
 * §15.5.1: Get the final result page hash for a given pgno after merge.
 *
 * Resolution order:
 * 1. If in resultHashByPgno → use it (merge already resolved this page)
 * 2. If NOT in remoteChanged → use local norm, else base hash
 * 3. If in remoteChanged → use remote hash
 *
 * Returns null if pgno > resultPageCount.
 */
export async function getFinalResultPageHash(
    plan: MergePlan,
    manifest: ManifestOperations,
    pgno: Pgno,
): Promise<PageHash | null> {
    if (pgno > plan.resultPageCount) return null

    if (plan.resultHashByPgno.has(pgno)) {
        return plan.resultHashByPgno.get(pgno) ?? null
    }

    if (!plan.remoteChanged.has(pgno)) {
        if (plan.liveLocalNorm.has(pgno)) {
            return plan.liveLocalNorm.get(pgno) ?? null
        }
        let baseHash = plan.baseHashes.get(pgno) ?? null
        if (baseHash == null) {
            baseHash = await manifest.getPageHashFromConcreteRoot(plan.baseRoot, pgno)
            plan.baseHashes.set(pgno, baseHash)
        }
        return baseHash
    }

    return plan.remoteHashes.get(pgno) ?? null
}

/**
 * Ensure the merge plan has at least one import blob when only page count
 * changes (no logical page changes).
 *
 * §15.5.1 / R60 / R21:
 * - If importBlobsByPgno already has entries, no carrier needed.
 * - If importBlobsByPgno is empty AND resultPageCount !== liveLocalPageCount,
 *   synthesize a carrier frame (recommended: page 1) so the WAL commit
 *   physically materializes target-EOF via nPage.
 *
 * The carrier bytes come from stagedCas, durable getBlob, or rpcGetBlob.
 */
export async function ensureMetadataCarrierFrameIfNeeded(
    plan: MergePlan,
    runtime: MetadataCarrierRuntime,
    manifest: ManifestOperations,
): Promise<void> {
    setMergePlanDebugStage(manifest, 'ensure-metadata-carrier:enter')
    const mustCarryPage1Metadata =
        plan.resultPageCount !== plan.liveLocalPageCount
        || plan.importBlobsByPgno.has(1 as Pgno)
    if (!mustCarryPage1Metadata) return

    // §15.5.1: carrier frame is page 1
    const carrierPgno = 1 as Pgno
    if (carrierPgno > plan.resultPageCount) {
        throw new Error('INVALID_METADATA_CARRIER_PAGECOUNT')
    }

    const existingPage1 = plan.importBlobsByPgno.get(carrierPgno)
    if (existingPage1 != null) {
        installPage1MetadataCarrier(plan, runtime, manifest, existingPage1)
        setMergePlanDebugStage(manifest, 'ensure-metadata-carrier:done')
        return
    }

    setMergePlanDebugStage(manifest, 'ensure-metadata-carrier:resolve-final-hash')
    const carrierHash = await getFinalResultPageHash(plan, manifest, carrierPgno)
    if (carrierHash == null) {
        throw new Error('MISSING_METADATA_CARRIER_HASH')
    }

    setMergePlanDebugStage(manifest, `ensure-metadata-carrier:load-blob:${carrierHash}:staged`)
    const carrierBytes =
        runtime.stagedCas.get(carrierHash)
        ?? (
            setMergePlanDebugStage(manifest, `ensure-metadata-carrier:load-blob:${carrierHash}:durable`),
            await runtime.getBlob(carrierHash)
        )
        ?? (
            setMergePlanDebugStage(manifest, `ensure-metadata-carrier:load-blob:${carrierHash}:remote`),
            await runtime.rpcGetBlob(carrierHash)
        )

    if (carrierBytes == null) {
        throw new Error('MISSING_METADATA_CARRIER_BLOB')
    }

    setMergePlanDebugStage(manifest, `ensure-metadata-carrier:install-blob:${carrierHash}`)
    installPage1MetadataCarrier(plan, runtime, manifest, carrierBytes)
    setMergePlanDebugStage(manifest, 'ensure-metadata-carrier:done')
}

function installPage1MetadataCarrier(
    plan: MergePlan,
    runtime: MetadataCarrierRuntime,
    manifest: ManifestOperations,
    page1Bytes: Uint8Array,
): void {
    const hashPage = manifest.hashPage
    if (hashPage == null) {
        throw new Error('MISSING_METADATA_CARRIER_HASHER')
    }

    const normalized = normalizePage1HeaderForResultPageCount(page1Bytes, plan.resultPageCount)
    const normalizedHash = hashPage(normalized)
    const page1 = 1 as Pgno

    plan.importBlobsByPgno.set(page1, normalized)
    plan.importBlobsByHash.set(normalizedHash, normalized)
    plan.resultHashByPgno.set(page1, normalizedHash)
    runtime.stagedCas.set(normalizedHash, normalized)
}

function normalizePage1HeaderForResultPageCount(
    page1Bytes: Uint8Array,
    resultPageCount: number,
): Uint8Array {
    if (page1Bytes.byteLength < 100) {
        throw new Error('INVALID_METADATA_CARRIER_PAGE1')
    }

    const normalized = page1Bytes.slice()
    const view = new DataView(
        normalized.buffer,
        normalized.byteOffset,
        normalized.byteLength,
    )
    const changeCounter = view.getUint32(24, false)
    const dbSize = view.getUint32(28, false)
    const versionValidFor = view.getUint32(92, false)

    if (dbSize !== resultPageCount || versionValidFor !== changeCounter) {
        const nextChangeCounter = (changeCounter + 1) >>> 0
        view.setUint32(24, nextChangeCounter, false)
        view.setUint32(28, resultPageCount >>> 0, false)
        view.setUint32(92, nextChangeCounter, false)
    }

    return normalized
}
